<template>
  <div>
    <b-tabs>
      <b-tab
        title="Compulsory Documents"
        active
      >
        <app-collapse
          class="business-items"
          type="margin"
          accordion
        >
          <app-collapse-item
            title="Legal Registration Documents"
            class="card-item px-2"
          >
            <validation-observer
              ref="legalDocumentsRules"
              @submit.prevent="saveLegalRegDocuments"
            >
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Evidence of CAC Registration (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Evidence of CAC Registration"
                      rules="required"
                    >
                      <b-form-file
                        id="evidence-file-name"
                        v-model="dueDiligence.compulsoryDocument.legalRegDocuments.cacRegistration"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CAC_REGISTRATION)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Memorandum and Article of Association (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Memorandum and Article of Association"
                      rules="required"
                    >
                      <b-form-file
                        id="memorandum-file-name"
                        v-model="dueDiligence.compulsoryDocument.legalRegDocuments.articleOfAssociation"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.ARTICLE_OF_ASSOCIATION)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>

                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Operating License of CBN (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Operating License of CBN"
                      rules="required"
                    >
                      <b-form-file
                        id="operating-license-name"
                        v-model="dueDiligence.compulsoryDocument.legalRegDocuments.operatingLicense"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv  format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.OPERATING_LICENSE)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>

                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.legalRegDocuments.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveLegalRegDocuments"
                  />
                </div>

                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="legalRegDocuments.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="legalRegDocuments.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="legalRegDocuments.reviewedBy">
                    <strong>Reviewed By:</strong> {{ legalRegDocuments.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(legalRegDocuments.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('LegalAndRegistrationDocument', legalRegDocuments.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="legalDocumentRef"
              :busy="isBusy"
              :items="legalDocumentList"
              responsive
              :fields="legalDocumentHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(evidenceOfCACRegistration)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.evidenceOfCACRegistration, 'Evidence Of CAC Registration')"
                >
                  View Document
                </div>
              </template>
              <template #cell(memorandum)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.memorandum, 'Memorandum')"
                >
                  View Document
                </div>
              </template>
              <template #cell(operatingLicense)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.operatingLicense, 'Operating License')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <app-collapse-item
            title="Board and Management Information"
            class="card-item px-2"
          >
            <validation-observer ref="boardManagementRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="4"
                  >
                    <InputField
                      id="last-name"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.lastName"
                      label="Last Name"
                      placeholder="Enter last name"
                      type="text"
                      error-name="Last Name"
                      rules="required"
                      for="last-name"
                      :readonly="isReadonly"
                      onpaste="return false;"
                      :is-allow-special-characters="false"
                    />
                  </b-col>
                  <b-col
                    md="4"
                  >
                    <InputField
                      id="first-name"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.firstName"
                      label="First Name"
                      placeholder="Enter first name"
                      type="text"
                      error-name="First Name"
                      rules="required"
                      for="first-name"
                      :readonly="isReadonly"
                      onpaste="return false;"
                      :is-allow-special-characters="false"
                    />
                  </b-col>
                  <b-col
                    md="4"
                  >
                    <InputField
                      id="middle-name"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.middleName"
                      label="Middle Name"
                      placeholder="Enter middile name"
                      type="text"
                      for="middle-name"
                      :readonly="isReadonly"
                      onpaste="return false;"
                      :is-allow-special-characters="false"
                    />
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Profile/CV Upload (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Profile/CV Upload"
                      rules="required"
                    >
                      <b-form-file
                        id="profile-CV-Upload-file-name"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.cvUpload"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CV_UPLOAD)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      CBN Approval Evidence (Board/Management) (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="CBN Approval Evidence"
                      rules="required"
                    >
                      <b-form-file
                        id="CBN-approval-evidence-file-name"
                        v-model="dueDiligence.compulsoryDocument.boardManagement.cbnApprovalEvidence"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CBN_APPROVAL_EVIDENCE)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="4"
                  >
                    <InputField
                      id="designation"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.designation"
                      label="Designation"
                      placeholder="Enter designation"
                      type="text"
                      error-name="Designation"
                      rules="required"
                      for="designation"
                      :readonly="isReadonly"
                      onpaste="return false;"
                      :is-allow-special-characters="false"
                    />
                  </b-col>
                  <b-col md="4">
                    <DatePickerField
                      id="year-appointed"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.yearAppointed"
                      :min-date="new Date()"
                      label="Year Appointed"
                      error-name="Year Appointed"
                      rules="required"
                      :config="dueDiligenceDatePickerConfig"
                      placeholder="Year Appointed"
                      :disabled="isReadonly"
                      :on-change="onSelectYearAppointed"
                    />
                  </b-col>

                  <b-col
                    md="4"
                  >
                    <InputField
                      id="duration"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.duration"
                      label="Duration"
                      placeholder=""
                      type="text"
                      for="duration"
                      onpaste="return false;"
                      readonly
                    />
                  </b-col>
                </b-row>

                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.boardManagement.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <div class="w-100 d-flex justify-content-end">

                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveBoardManagementInfo"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="boardManagement.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="boardManagement.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="boardManagement.reviewedBy">
                    <strong>Reviewed By:</strong> {{ boardManagement.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(boardManagement.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('BoardAndManagementInformation', boardManagement.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="boardManagementRef"
              :busy="isBusy"
              :items="boardManagementList"
              responsive
              :fields="boardManagementHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >

              <template #cell(cvUpload)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.cvUpload, 'CV Upload')"
                >
                  View Document
                </div>
              </template>
              <template #cell(cbnApprovalEvidence)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.cbnApprovalEvidence, 'CBN Approval Evidence')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <app-collapse-item
            title="Leadership and Governance Structure"
            class="card-item px-2"
          >
            <validation-observer ref="leadershipStructureRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Bank Organisation Structure (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Bank Organisation Structure"
                      rules="required"
                    >
                      <b-form-file
                        id="bank-organisation-structure-file-name"
                        v-model="dueDiligence.compulsoryDocument.leadershipGovernance.bankOrganisationStructure"
                        label="Select File (.jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.BANK_ORGANISATION_STRUCTURE)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Bank's Organisational Chart (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Bank's Organisational Chart"
                      rules="required"
                    >
                      <b-form-file
                        id="bank-organisational-chart-file-name"
                        v-model="dueDiligence.compulsoryDocument.leadershipGovernance.bankOrganisationChart"
                        label="Select File (.jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .jpeg, .png, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.BANK_ORGANISATION_CHART)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.leadershipGovernance.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveLegalGovernance"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="leadershipGovernance.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="leadershipGovernance.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="leadershipGovernance.reviewedBy">
                    <strong>Reviewed By:</strong> {{ leadershipGovernance.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(leadershipGovernance.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('LeadershipAndGovernanceStructure', leadershipGovernance.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="leadershipAndGovernanceRef"
              :busy="isBusy"
              :items="leadershipAndGovernanceList"
              responsive
              :fields="leadershipAndGovernanceHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(bankOrganistionStructure)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.bankOrganistionStructure, 'Bank Organistion Structure')"
                >
                  View Document
                </div>
              </template>
              <template #cell(banksOrganisationalChart)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.banksOrganisationalChart, 'Banks Organisational Chart')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <app-collapse-item
            title="Financial Statements"
            class="card-item px-2"
          >
            <validation-observer ref="financialStatementRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Audited Financial Statement Year 2024 (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Audited Financial Statement Year 2024"
                      rules="required"
                    >
                      <b-form-file
                        id="audited-financial-statement-file-name-1"
                        v-model="dueDiligence.compulsoryDocument.financialStatements.auditedFinancialStatement1"
                        v-b-tooltip.hover.bottom="'You are required to upload the CBN approved version'"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.AUDITED_FINANCIAL_STATEMENT_1)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Audited Financial Statement Year 2023 (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Audited Financial Statement Year 2023"
                      rules="required"
                    >
                      <b-form-file
                        id="audited-financial-statement-file-name-2"
                        v-model="dueDiligence.compulsoryDocument.financialStatements.auditedFinancialStatement2"
                        v-b-tooltip.hover.bottom="'You are required to upload the CBN approved version'"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.AUDITED_FINANCIAL_STATEMENT_2)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Audited Financial Statement Year 2022 (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Audited Financial Statement Year 2022"
                      rules="required"
                    >
                      <b-form-file
                        id="audited-financial-statement-file-name-3"
                        v-model="dueDiligence.compulsoryDocument.financialStatements.auditedFinancialStatement3"
                        v-b-tooltip.hover.bottom="'You are required to upload the CBN approved version'"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.AUDITED_FINANCIAL_STATEMENT_3)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.financialStatements.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveFinancialStatements"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="financialStatements.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="financialStatements.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="financialStatements.reviewedBy">
                    <strong>Reviewed By:</strong> {{ financialStatements.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(financialStatements.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('FinancialStatement', financialStatements.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="financialStatementRef"
              :busy="isBusy"
              :items="financialStatementList"
              responsive
              :fields="financialStatementHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(auditedFinancialStatement1)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.auditedFinancialStatement1, 'Audited Financial Statement Year 2024')"
                >
                  View Document
                </div>
              </template>
              <template #cell(auditedFinancialStatement2)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.auditedFinancialStatement3, 'Audited Financial Statement Year 2023')"
                >
                  View Document
                </div>
              </template>
              <template #cell(auditedFinancialStatement3)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.auditedFinancialStatement3, 'Audited Financial Statement Year 2022')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <!-- Recent Financial Performance -->
          <app-collapse-item
            title="Recent Financial Performance"
            class="card-item px-2"
          >
            <validation-observer ref="recentFinancialPerformanceRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Latest Quarterly Management Accounts & Prudential Ratios (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Latest Quarterly Management Accounts & Prudential Ratios"
                      rules="required"
                    >
                      <b-form-file
                        id="latest-financial-performance"
                        v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.latestFinancialPerformance"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.LASTEST_FINANCIAL_PERFORMANCE)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                  >
                    <ValidationObserver>
                      <InputNumberField
                        id="capital-adequacy-ratio"
                        v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.capitalAdequacyRatio"
                        label="Capital Adequacy Ratio (%)"
                        placeholder="Enter capital adequacy ratio"
                        type="number"
                        rules="required|max-value:100|positive"
                        error-name="Capital Adequacy Ratio"
                        :readonly="isReadonly"
                        for="capital-adequacy-ratio"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col
                    md="6"
                  >
                    <ValidationObserver>
                      <InputNumberField
                        id="non-performing-loan-ratio"
                        v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.nonPerformingLoanRatio"
                        label="Non Performing Loan Ratio (%)"
                        placeholder="Enter non performing loan ratio"
                        type="number"
                        rules="required|max-value:100|positive"
                        error-name="Non Performing Loan Ratio"
                        :readonly="isReadonly"
                        for="non-performing-loan-ratio"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col
                    md="6"
                  >
                    <ValidationObserver>
                      <InputNumberField
                        id="liquidity-ratio"
                        v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.liquidityRatio"
                        label="Liquidity Ratio (%)"
                        placeholder="Enter liquidity ratio"
                        type="number"
                        rules="required|max-value:100|positive"
                        error-name="Liquidity Ratio"
                        :readonly="isReadonly"
                        for="liquidity-ratio"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col
                    md="6"
                  >
                    <ValidationObserver>
                      <InputNumberField
                        id="loan to deposit ratio"
                        v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.loanToDepositRatio"
                        label="Loan To Deposit Ratio (%)"
                        placeholder="Enter loan to deposit ratio"
                        type="number"
                        rules="required|max-value:100|positive"
                        error-name="Loan To Deposit Ratio"
                        for="loan to deposit ratio"
                        :readonly="isReadonly"
                      />
                    </ValidationObserver>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.recentFinancialPerformance.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveRecentFinancialPerformance"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="recentFinancialPerformance.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="recentFinancialPerformance.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="recentFinancialPerformance.reviewedBy">
                    <strong>Reviewed By:</strong> {{ recentFinancialPerformance.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(recentFinancialPerformance.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('RecentFinancialPerformance', recentFinancialPerformance.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="recentFinancialPerformanceRef"
              :busy="isBusy"
              :items="recentFinancialPerformanceList"
              responsive
              :fields="recentFinancialPerformanceHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(latestFinancialPerformance)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.latestFinancialPerformance, 'Latest Financial Performance')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Recent Financial Performance -->

          <!-- Credit Risk Management -->
          <app-collapse-item
            title="Credit Risk Management"
            class="card-item px-2"
          >
            <validation-observer ref="creditRiskManagementRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Credit Risk Strategy and Annual Portfolio Plan (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Credit Risk Strategy"
                      rules="required"
                    >
                      <b-form-file
                        id="credit-risk-strategy"
                        v-model="dueDiligence.compulsoryDocument.creditRiskManagement.creditRiskStrategyAndAnnualPortfolioPlan"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CREDIT_RISK_MANAGEMENT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.creditRiskManagement.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    @click="saveCreditRiskManagement"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="creditRiskManagement.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="creditRiskManagement.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="creditRiskManagement.reviewedBy">
                    <strong>Reviewed By:</strong> {{ creditRiskManagement.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(creditRiskManagement.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('CreditRiskManagement', creditRiskManagement.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="creditRiskManagementRef"
              :busy="isBusy"
              :items="creditRiskManagementList"
              responsive
              :fields="creditRiskManagementHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(creditRiskStrategyAndAnnualPortfolioPlan)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.creditRiskStrategyAndAnnualPortfolioPlan, 'Credit Risk Strategy And Annual Portfolio Plan')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <!-- Risk Management Framework -->
          <app-collapse-item
            title="Risk Management Framework"
            class="card-item px-2"
          >
            <validation-observer ref="riskManagementFrameworkRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Risk Management Framework (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Risk Management Framework"
                      rules="required"
                    >
                      <b-form-file
                        id="risk-managment-framework"
                        v-model="dueDiligence.compulsoryDocument.riskManagementFramework.riskManagementFrameworkUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_FRAMEWORK)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Policies (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Policies"
                      rules="required"
                    >
                      <b-form-file
                        id="policies"
                        v-model="dueDiligence.compulsoryDocument.riskManagementFramework.policiesUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_POLICIES)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Limits (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Limits"
                      rules="required"
                    >
                      <b-form-file
                        id="limits"
                        v-model="dueDiligence.compulsoryDocument.riskManagementFramework.limitsUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_LIMITS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Manuals (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Manuals"
                      rules="required"
                    >

                      <b-form-file
                        id="manuals"
                        v-model="dueDiligence.compulsoryDocument.riskManagementFramework.ManualUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_MANUAL)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>

                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.riskManagementFramework.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveRiskManagementFramework"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="riskManagementFramework.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="riskManagementFramework.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="riskManagementFramework.reviewedBy">
                    <strong>Reviewed By:</strong> {{ riskManagementFramework.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(riskManagementFramework.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('RiskManagementFramework', riskManagementFramework.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="riskManagementFrameworkRef"
              :busy="isBusy"
              :items="riskManagementFrameworkList"
              responsive
              :fields="riskManagementFrameworkHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(riskManagementFrameworkUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.riskManagementFrameworkUrl, 'Risk Management Framework')"
                >
                  View Document
                </div>
              </template>
              <template #cell(policiesUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.policiesUrl, 'policies')"
                >
                  View Document
                </div>
              </template>
              <template #cell(limitsUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.limitsUrl, 'Limits')"
                >
                  View Document
                </div>
              </template>
              <template #cell(manualUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.manualUrl, 'Manuals')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Risk Management Framework -->

          <!-- Loan Portfolio Details -->
          <app-collapse-item
            title="Loan Portfolio Details"
            class="card-item px-2"
          >
            <validation-observer ref="loanPortfolioDetailsRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Most Recent Credit Portfolio Report (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Most Recent Credit Portfolio Report"
                      rules="required"
                    >

                      <b-form-file
                        id="loan-portfolio-details"
                        v-model="dueDiligence.compulsoryDocument.loanPortfolioDetails.creditPortfolioReport"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CREDIT_PORTFOLIO_REPORT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.loanPortfolioDetails.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveLoanPortfolioDetails"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="loanPortfolioDetails.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="loanPortfolioDetails.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="loanPortfolioDetails.reviewedBy">
                    <strong>Reviewed By:</strong> {{ loanPortfolioDetails.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(loanPortfolioDetails.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('LoanPortfolioDetails', loanPortfolioDetails.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="loanPortfolioDetailsRef"
              :busy="isBusy"
              :items="loanPortfolioDetailsList"
              responsive
              :fields="loanPortfolioDetailsHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(creditPortfolioReport)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.creditPortfolioReport, 'Credit Portfolio Report')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Loan Portfolio Details -->

          <!-- AML/CFT Compliance -->
          <app-collapse-item
            title="AML/CFT Compliance"
            class="card-item px-2"
          >
            <validation-observer ref="AML_CFTComplianceRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Anti-Money Laundering (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Anti-Money Laundering"
                      rules="required"
                    >
                      <b-form-file
                        id="anti-money-laundry"
                        v-model="dueDiligence.compulsoryDocument.aMLCFTCompliance.antiMoneyLaundryUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.ANTI_MONEY_LAUNDRY)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Counter-Terrorist Financing Policies And Procedures (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Counter-Terrorist Financing Policies And Procedures"
                      rules="required"
                    >
                      <b-form-file
                        id="counter-terrorist-financing"
                        v-model="dueDiligence.compulsoryDocument.aMLCFTCompliance.CounterTerroristFinancingPoliciesUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.COUNTER_TERRORIST_FINANCING)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>

                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.aMLCFTCompliance.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveAMLCFTCompliance"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="aMLCFTCompliance.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="aMLCFTCompliance.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="aMLCFTCompliance.reviewedBy">
                    <strong>Reviewed By:</strong> {{ aMLCFTCompliance.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(aMLCFTCompliance.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('AMLCFTCompliance', aMLCFTCompliance.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="aMLCFTComplianceRef"
              :busy="isBusy"
              :items="aMLCFTComplianceList"
              responsive
              :fields="aMLCFTComplianceHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(antiMoneyLaundryUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.antiMoneyLaundryUrl, 'Anti-Money Laundering')"
                >
                  View Document
                </div>
              </template>
              <template #cell(counterTerroristFinancingPoliciesUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.counterTerroristFinancingPoliciesUrl, 'Counter Terrorist Financing Policies')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- AML/CFT Compliance -->

          <!-- Regulatory Compliance -->
          <app-collapse-item
            title="Regulatory Compliance"
            class="card-item px-2"
          >
            <validation-observer ref="regulatoryComplianceRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Evidence of Regulatory Returns Rendition (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Evidence of Regulatory Returns Rendition"
                      rules="required"
                    >
                      <b-form-file
                        id="regulatory-returns-rendition"
                        v-model="dueDiligence.compulsoryDocument.regulatoryCompliance.regulatoryReturnsRenditionUrl"
                        label="Select File (jpg, png, jpeg, docx, pdf, xlsx, csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept="jpg, png, jpeg, docx, pdf, xlsx, csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.REGULATORY_RETURNS_RENDITION)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.regulatoryCompliance.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveRegulatoryCompliance"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="regulatoryCompliance.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="regulatoryCompliance.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="regulatoryCompliance.reviewedBy">
                    <strong>Reviewed By:</strong> {{ regulatoryCompliance.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(regulatoryCompliance.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('RegulatoryCompliance', regulatoryCompliance.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="regulatoryComplianceRef"
              :busy="isBusy"
              :items="regulatoryComplianceList"
              responsive
              :fields="regulatoryComplianceHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(regulatoryReturnsRenditionUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.regulatoryReturnsRenditionUrl, 'Regulatory Returns Rendition')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Regulatory Compliance -->

          <!-- Regulatory Examination Reports -->
          <app-collapse-item
            title="Regulatory Examination Reports"
            class="card-item px-2"
          >
            <validation-observer ref="regulatoryExaminationReportsRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      CBN Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="CBN Examination Reports"
                      rules="required"
                    >
                      <b-form-file
                        id="cbn-examination-reports"
                        v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.cBNExaminationReportsUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CBN_EXAMINATION_REPORTS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      NDIC Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="NDIC Examination Reports"
                      rules="required"
                    >
                      <b-form-file
                        id="ndic-examination-reports"
                        v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.nDICExaminationReportUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        error-name="NDIC Examination Reports"
                        rules="required"
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.NDIC_EXAMINATION_REPORTS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Management Response to CBN Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Management Response to CBN Examination Reports"
                      rules="required"
                    >
                      <b-form-file
                        id="management-response-to-examination-report"
                        v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.managementResponseToExaminationReportUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.MANAGEMENT_RESPONSE_TO_EXAMINATION_REPORTS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Management Response to NDIC Examination Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Management Response to NDIC Examination Reports"
                      rules="required"
                    >
                      <b-form-file
                        id="management-response-to-ndic-examination-report"
                        v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.managementResponseToNDICExaminationReportsUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.MANAGEMENT_RESPONSE_TO_NDIC_EXAMINATION_REPORTS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.regulatoryExaminationReports.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveRegulatoryExaminationReports"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="regulatoryExaminationReports.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="regulatoryExaminationReports.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="regulatoryExaminationReports.reviewedBy">
                    <strong>Reviewed By:</strong> {{ regulatoryExaminationReports.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(regulatoryExaminationReports.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('RegulatoryExaminationReports', regulatoryExaminationReports.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="regulatoryExaminationReportsRef"
              :busy="isBusy"
              :items="regulatoryExaminationReportsList"
              responsive
              :fields="regulatoryExaminationReportsHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(cBNExaminationReportsUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.cBNExaminationReportsUrl, 'CBN Examination Reports')"
                >
                  View Document
                </div>
              </template>
              <template #cell(nDICExaminationReportUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.nDICExaminationReportUrl, 'NDIC Examination Report')"
                >
                  View Document
                </div>
              </template>
              <template #cell(managementResponseToExaminationReportUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.managementResponseToExaminationReportUrl, 'Management Response To Examination Report')"
                >
                  View Document
                </div>
              </template>
              <template #cell(managementResponseToNDICExaminationReportsUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.managementResponseToNDICExaminationReportsUrl, 'Management Response To NDIC Examination Reports')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Regulatory Examination Reports -->

          <!-- Risk And Audit Structure -->
          <app-collapse-item
            title="Risk And Audit Structure"
            class="card-item px-2"
          >
            <validation-observer ref="riskAndAuditStructureRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Department Structure (Organogram) (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Department Structure (Organogram)"
                      rules="required"
                    >
                      <b-form-file
                        id="organogram-url"
                        v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.organogramUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.DEPARTMENT_STRUCTURE_ORGANOGRAM)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                      <small class="text-muted">Please upload the organizational chart illustrating the structure of the Risk Management and Internal Audit departments, including reporting lines.</small>
                    </validationprovider></b-col>
                </b-row>

                <b-row>
                  <b-col
                    md="12"
                    class="d-flex align-items-center justify-content-between mb-1 mt-0"
                  >
                    <span class="section-label">
                      Staff Information - Risk Management Unit
                    </span>
                    <Button
                      label="Add"
                      size="md"
                      icon="PlusIcon"
                      :disabled="isReadonly"
                      @click="addNewRiskManagementUnitStaffDetails"
                    />
                  </b-col>
                </b-row>

                <b-row
                  v-for="(item, index) in dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.staffDetails"
                  :key="`risk-staff-${index}`"
                  class="match-height"
                >
                  <b-col md="3">
                    <ValidationObserver>
                      <InputField
                        id="staff-full-name"
                        v-model="item.fullName"
                        label="Full Name"
                        type="text"
                        error-name="Full Name"
                        :readonly="isReadonly"
                        rules="required"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col md="3">
                    <ValidationObserver>
                      <InputField
                        id="designation"
                        v-model="item.designation"
                        label="Designation"
                        type="text"
                        error-name="Designation"
                        :readonly="isReadonly"
                        rules="required"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col md="3">
                    <ValidationObserver>
                      <SelectField
                        id="qualifications"
                        v-model="item.qualifications"
                        label="Qualifications"
                        multiple
                        :reduce="option => option.value"
                        :options="qualificationsOptions"
                        item="label"
                        placeholder="Select qualifications"
                        error-name="Qualifications"
                        :readonly="isReadonly"
                        rules="required"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col md="2">
                    <ValidationObserver>
                      <InputField
                        id="relevant-working-experience"
                        v-model="item.relevantWorkingExperience"
                        label="Relevant Working Experience"
                        type="text"
                        error-name="Relevant Working Experience"
                        rules="required"
                        :readonly="isReadonly"
                      />
                    </ValidationObserver>
                  </b-col>
                  <b-col
                    v-if="!isReadonly"
                    md="1"
                    class="mt-2"
                  >
                    <feather-icon
                      v-b-tooltip.hover.bottom="'Remove'"
                      icon="XIcon"
                      class="cursor-pointer text-danger"
                      @click="removeRiskManagementUnitStaffDetails(index)"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Document (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Document"
                      rules="required"
                    >
                      <b-form-file
                        id="risk-management-unit-staff-info"
                        v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.documentUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_UNIT_STAFF_DETAILS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    md="12"
                    class="d-flex align-items-center justify-content-between mb-1 mt-0"
                  >
                    <span class="section-label">
                      Staff Information - Internal Audit Function
                    </span>
                    <Button
                      label="Add"
                      size="md"
                      icon="PlusIcon"
                      :disabled="isReadonly"
                      @click="addNewInternalAuditFunctionStaffDetails"
                    />
                  </b-col>
                </b-row>

                <b-row
                  v-for="(item, index) in dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.staffDetails"
                  :key="`internal-staff-${index}`"
                  class="match-height"
                >
                  <b-col md="3">
                    <InputField
                      id="staff-full-name"
                      v-model="item.fullName"
                      label="Full Name"
                      type="text"
                      error-name="Full Name"
                      rules="required"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="3">
                    <InputField
                      id="designation"
                      v-model="item.designation"
                      label="Designation"
                      type="text"
                      error-name="Designation"
                      rules="required"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="3">
                    <SelectField
                      id="qualifications"
                      v-model="item.qualifications"
                      label="Qualifications"
                      multiple
                      :reduce="option => option.value"
                      :options="qualificationsOptions"
                      item="label"
                      placeholder="Select qualifications"
                      error-name="Qualifications"
                      rules="required"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col md="2">
                    <InputField
                      id="relevant-working-experience"
                      v-model="item.relevantWorkingExperience"
                      label="Relevant Working Experience"
                      type="text"
                      error-name="Relevant Working Experience"
                      rules="required"
                      :readonly="isReadonly"
                    />
                  </b-col>
                  <b-col
                    v-if="!isReadonly"
                    md="1"
                    class="mt-2"
                  >
                    <feather-icon
                      v-b-tooltip.hover.bottom="'Remove'"
                      icon="XIcon"
                      class="cursor-pointer text-danger"
                      @click="removeInternalAuditFunctionStaffDetails(index)"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Document (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Document"
                      rules="required"
                    >
                      <b-form-file
                        id="internal-audit-function-staff-info"
                        v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.documentUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_FUNCTION_STAFF_DETAILS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.riskAndAuditStructure.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveRiskAndAuditStructure"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="riskAndAuditStructure.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="riskAndAuditStructure.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="riskAndAuditStructure.reviewedBy">
                    <strong>Reviewed By:</strong> {{ riskAndAuditStructure.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(riskAndAuditStructure.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('RiskAndAuditStructure', riskAndAuditStructure.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="riskAndAuditStructureRef"
              :busy="isBusy"
              :items="riskAndAuditStructureList"
              responsive
              :fields="riskAndAuditStructureHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(organogramUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.organogramUrl, 'Organogram')"
                >
                  View Document
                </div>
              </template>
              <template #cell(riskManagementUnitStaffDetails)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.riskManagementUnitStaffDetails.documentUrl, 'Risk Management Unit Staff Details')"
                >
                  View Document
                </div>
              </template>
              <template #cell(internalAuditFunctionStaffDetails)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.internalAuditFunctionStaffDetails.documentUrl, 'Internal Audit Function Staff Details')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Risk And Audit Structure -->

          <!-- Ongoing Risk Monitoring -->
          <app-collapse-item
            title="Ongoing Risk Monitoring"
            class="card-item px-2"
          >
            <validation-observer ref="ongoingRiskMonitoringRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Risk Management Reports To Board (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Risk Management Reports To Board"
                      rules="required"
                    >
                      <b-form-file
                        id="risk-management-reports-board"
                        v-model="dueDiligence.compulsoryDocument.ongoingRiskMonitoring.riskManagementReportsToBoardUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RISK_MANAGEMENT_REPORTS_BOARD)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.ongoingRiskMonitoring.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveOngoingRiskMonitoring"
                  />
                </div>
                <b-row class="match-height">
                  <b-col
                    v-if="lmsRole === 'Admin'"
                    md="12"
                  >
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="ongoingRiskMonitoring.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="ongoingRiskMonitoring.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="ongoingRiskMonitoring.reviewedBy">
                    <strong>Reviewed By:</strong> {{ ongoingRiskMonitoring.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(ongoingRiskMonitoring.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('OngoingRiskMonitoring', ongoingRiskMonitoring.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="ongoingRiskMonitoringRef"
              :busy="isBusy"
              :items="ongoingRiskMonitoringList"
              responsive
              :fields="ongoingRiskMonitoringHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(riskManagementReportsToBoardUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.riskManagementReportsToBoardUrl, 'Risk Management Reports To Board')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Ongoing Risk Monitoring -->

          <!-- Internal Audit Findings -->
          <app-collapse-item
            title="Internal Audit Findings"
            class="card-item px-2"
          >
            <validation-observer ref="internalAuditFindingsRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      {{ `Internal Audits Report ${lastYear} (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)` }}
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="`Internal Audits Report ${lastYear}`"
                      rules="required"
                    >
                      <b-form-file
                        id="internal-audits-report_last-year"
                        v-model="dueDiligence.compulsoryDocument.internalAuditFindings.internalAuditsReportUrlLastYear"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_REPORTS_LAST_YEAR)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      {{ `Internal Audits Report ${lastTwoYears} (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)` }}
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="`Internal Audits Report ${lastTwoYears}`"
                      rules="required"
                    >
                      <b-form-file
                        id="internal-audits-report_last_two_years"
                        v-model="dueDiligence.compulsoryDocument.internalAuditFindings.internalAuditsReportUrlTwoYearsAgo"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_REPORTS_LAST_TWO_YEARS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      {{ `Internal Audits Report ${lastThreeYears} (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)` }}
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      :name="`Internal Audits Report ${lastThreeYears}`"
                      rules="required"
                    >
                      <b-form-file
                        id="internal-audits-report_last_three_years"
                        v-model="dueDiligence.compulsoryDocument.internalAuditFindings.internalAuditsReportUrlThreeYearsAgo"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_AUDIT_REPORTS_LAST_THREE_YEARS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.internalAuditFindings.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveInternalAuditFindings"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="internalAuditFindings.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="internalAuditFindings.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="internalAuditFindings.reviewedBy">
                    <strong>Reviewed By:</strong> {{ internalAuditFindings.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(internalAuditFindings.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('InternalAuditFindings', internalAuditFindings.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="internalAuditFindingsRef"
              :busy="isBusy"
              :items="internalAuditFindingsList"
              responsive
              :fields="internalAuditFindingsHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(internalAuditsReportUrlLastYear)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.internalAuditsReportUrlLastYear, 'Internal Audits Report Last Year')"
                >
                  View Document
                </div>
              </template>
              <template #cell(internalAuditsReportUrlTwoYearsAgo)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.internalAuditsReportUrlTwoYearsAgo, 'Internal Audits Report Two Years Ago')"
                >
                  View Document
                </div>
              </template>
              <template #cell(internalAuditsReportUrlThreeYearsAgo)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.internalAuditsReportUrlThreeYearsAgo, 'Internal Audits Report Three Years Ago')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Internal Audit Findings -->

          <!-- Corporate Governance -->
          <app-collapse-item
            title="Corporate Governance"
            class="card-item px-2"
          >
            <validation-observer ref="corporateGovernanceRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Corporate Governance Policies & Code of Conduct (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Corporate Governance Policies & Code of Conduct"
                      rules="required"
                    >
                      <b-form-file
                        id="corporate-governance-policies-conduct"
                        v-model="dueDiligence.compulsoryDocument.corporateGovernance.corporateGovernanceUrl"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CORPRATE_GOVERNANCE_REPORT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.corporateGovernance.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveCorporateGovernance"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="corporateGovernance.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="corporateGovernance.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="corporateGovernance.reviewedBy">
                    <strong>Reviewed By:</strong> {{ corporateGovernance.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(corporateGovernance.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('CorporateGovernance', corporateGovernance.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="corporateGovernanceRef"
              :busy="isBusy"
              :items="corporateGovernanceList"
              responsive
              :fields="corporateGovernanceHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(corporateGovernanceUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.corporateGovernanceUrl, 'Corporate Governance')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Corporate Governance -->

          <!-- Collateral Management -->
          <!-- <app-collapse-item
            title="Collateral Management"
            class="card-item px-2"
          >
            <validation-observer ref="collateralManagementRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Collateral Valuation (Format accepted: PDF, DOC, DOCX)
                    </p>
                    <b-form-file
                      id="collateral-valuation"
                      v-model="dueDiligence.compulsoryDocument.collateralManagement.collateralValuationUrl"
                      label="Select File (.pdf, jpg, png format only)"
                      :state="Boolean(dueDiligence.compulsoryDocument.collateralManagement.collateralValuationUrl)"
                      placeholder="Choose a file or drop it here..."
                      drop-placeholder="Drop file here..."
                      error-name="Collateral Valuation"
                      rules="required"
                      accept=".pdf, .doc, .docx"
                      :disabled="isReadonly"
                      @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.COLLATERAL_VALUATION)"
                    />
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      National Collateral Registry (Format accepted: PDF, DOC, DOCX)
                    </p>
                    <b-form-file
                      id="national-collateral-register"
                      v-model="dueDiligence.compulsoryDocument.collateralManagement.nationalCollateralRegistryUrl"
                      label="Select File (.pdf, jpg, png format only)"
                      :state="Boolean(dueDiligence.compulsoryDocument.collateralManagement.nationalCollateralRegistryUrl)"
                      placeholder="Choose a file or drop it here..."
                      drop-placeholder="Drop file here..."
                      error-name=" National Collateral Registry"
                      rules="required"
                      accept=".pdf, .doc, .docx"
                      :disabled="isReadonly"
                      @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.NATIONAL_COLLATERAL_REGISTER)"
                    />
                  </b-col>
                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.collateralManagement.comment"
                      rows="2"
                      label="Comment"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveCollateralManagement"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="collateralManagementRef"
              :busy="isBusy"
              :items="collateralManagementList"
              responsive
              :fields="collateralManagementHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(collateralValuationUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.collateralValuationUrl, 'Collateral Valuation')"
                >
                  View Document
                </div>
              </template>
              <template #cell(nationalCollateralRegistryUrl)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.nationalCollateralRegistryUrl, 'National Collateral Registry')"
                >
                  View Document
                </div>
              </template>
              <template #cell(dateModified)="data">
                {{ data.value ? toDateWithYear(data.value) : '' }}
              </template>
            </b-table>
          </app-collapse-item> -->
          <!-- Corporate Governance -->

          <!-- Credit Bureau Agreement -->

          <app-collapse-item
            title="Credit Bureau Agreement"
            class="card-item px-2"
          >
            <validation-observer ref="creditBureauAgreementRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Evidence of Agreement Credit Bureaus (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Evidence of Agreement Credit Bureaus"
                      rules="required"
                    >
                      <b-form-file
                        id="evidence-of-agreement-file-name"
                        v-model="dueDiligence.compulsoryDocument.creditBureauAgreement.evidenceOfAgreement"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        multiple
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_AGREEMENT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.compulsoryDocument.creditBureauAgreement.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveCreditBureau"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="creditBureauAgreement.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="creditBureauAgreement.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="creditBureauAgreement.reviewedBy">
                    <strong>Reviewed By:</strong> {{ creditBureauAgreement.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(creditBureauAgreement.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('CreditBureauAgreement', creditBureauAgreement.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="creditBureauRef"
              :busy="isBusy"
              :items="creditBureauList"
              responsive
              :fields="creditBureauHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(evidenceOfAgreement)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.evidenceOfAgreement, 'Evidence Of Agreement')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <!-- Credit Bureau Agreement -->
        </app-collapse>
      </b-tab>

      <b-tab title="Supporting Documents">
        <app-collapse
          class="business-items"
          type="margin"
          accordion
        >
          <!-- Customer Complaints Handling -->
          <app-collapse-item
            title="Customer Complaints Handling"
            class="card-item px-2"
          >
            <validation-observer ref="customerComplaintsHandlingRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Customer Complaint Management System/Framework Bureaus(Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Customer Complaint Management System/Framework Bureaus"
                      rules="required"
                    >
                      <b-form-file
                        id="file-name"
                        v-model="dueDiligence.supportingDocument.customerComplaintsHandling.customerComplaintsManagement"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        multiple
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.CUSTOMER_COMPLAINTS_MANAGEMENT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                    <small class="text-muted">Please upload the document outlining your bank's Customer Complaints Management System and Framework. This may include policies, procedures, and process flow diagram</small>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.customerComplaintsHandling.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveCustomerComplaintsHandling"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="customerComplaintsHandling.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="customerComplaintsHandling.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="customerComplaintsHandling.reviewedBy">
                    <strong>Reviewed By:</strong> {{ customerComplaintsHandling.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(customerComplaintsHandling.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('CustomerComplaintsHandling', customerComplaintsHandling.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="customerComplaintHandlingRef"
              :busy="isBusy"
              :items="customerComplaintHandlingList"
              responsive
              :fields="customerComplaintHandlingHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(customerComplaintManagement)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.customerComplaintManagement, 'Customer Complaint Management')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <!-- Customer Complaints Handling -->

          <app-collapse-item
            title="Consumer Credit Programs"
            class="card-item px-2"
          >
            <validation-observer ref="consumerCreditRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Copies of Consumer Credit Product Programs (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Copies of Consumer Credit Product Programs"
                      rules="required"
                    >

                      <b-form-file
                        id="copies-of-consumer-file-name"
                        v-model="dueDiligence.supportingDocument.consumerCreditProgram.copiesConsumerCredit"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        multiple
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.COPIES_CONSUMER_CREDIT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.consumerCreditProgram.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveConsumerCreditProgram"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="consumerCreditProgram.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="consumerCreditProgram.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="consumerCreditProgram.reviewedBy">
                    <strong>Reviewed By:</strong> {{ consumerCreditProgram.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(consumerCreditProgram.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('CustomerCreditPrograms', consumerCreditProgram.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>
            <b-table
              ref="consumerCreditRef"
              :busy="isBusy"
              :items="consumerCreditList"
              responsive
              :fields="consumerCreditHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(copiesConsumerCredit)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.copiesConsumerCredit, 'Copies Consumer Credit')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <app-collapse-item
            title="Consumer Loan Pricing"
            class="card-item px-2"
          >
            <validation-observer ref="consumerLoanPricingRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Loan Pricing Strategy for Consumer Credit Customers (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Loan Pricing Strategy for Consumer Credit Customers"
                      rules="required"
                    >

                      <b-form-file
                        id="loan-pricing-strategy-file-name"
                        v-model="dueDiligence.supportingDocument.consumerLoanPricing.loanPricingStrategy"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.LOAN_PRICING_STRATEGY)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.consumerLoanPricing.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveConsumerLoanPricing"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="consumerLoanPricing.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="consumerLoanPricing.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="consumerLoanPricing.reviewedBy">
                    <strong>Reviewed By:</strong> {{ consumerLoanPricing.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(consumerLoanPricing.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('ConsumerLoanPricing', consumerLoanPricing.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>
            <b-table
              ref="consumerLoanPricingRef"
              :busy="isBusy"
              :items="consumerLoanPricingList"
              responsive
              :fields="consumerLoanPricingHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(loanPricingStrategy)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.loanPricingStrategy, 'Loan Pricing Strategy')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <app-collapse-item
            title="External Board Assessment"
            class="card-item px-2"
          >
            <validation-observer ref="externalBoardAssessmentRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Most recent External Board Evaluation/Assessment Report (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Most Recent External Board Evaluation/Assessment Report"
                      rules="required"
                    >
                      <b-form-file
                        id="external-board-evaluation-file-name"
                        v-model="dueDiligence.supportingDocument.externalBoardAssessment.recentExternalBoard"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        multiple
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RECENT_EXTERNAL_BOARD)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.externalBoardAssessment.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>
                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveExternalBoardAssessment"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="externalBoardAssessment.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="externalBoardAssessment.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="externalBoardAssessment.reviewedBy">
                    <strong>Reviewed By:</strong> {{ externalBoardAssessment.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(externalBoardAssessment.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('ExternalBoardAssessment', externalBoardAssessment.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="externalBoardAssessmentRef"
              :busy="isBusy"
              :items="externalBoardAssessmentList"
              responsive
              :fields="externalBoardAssessmentHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(mostRecentExternalBoardEvaluation)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.mostRecentExternalBoardEvaluation, 'Most Recent External Board Evaluation')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <app-collapse-item
            title="IT System Agreements"
            class="card-item px-2"
          >
            <validation-observer ref="itSystemAgreementsRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Signed Agreement with Banking Application and Other IT Vendor (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Signed Agreement with Banking Application and Other IT Vendor"
                      rules="required"
                    >
                      <b-form-file
                        id="signed-agreement-file-name"
                        v-model="dueDiligence.supportingDocument.itSystemAgreements.signedAgreement"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        multiple
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.SIGNED_AGREEMENT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.itSystemAgreements.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveITSystemAgreement"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="itSystemAgreements.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="itSystemAgreements.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="itSystemAgreements.reviewedBy">
                    <strong>Reviewed By:</strong> {{ itSystemAgreements.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(itSystemAgreements.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('ITSystemAgreements', itSystemAgreements.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="itSystemAgreementsRef"
              :busy="isBusy"
              :items="itSystemAgreementsList"
              responsive
              :fields="itSystemAgreementsHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(signedAgreement)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.signedAgreement, 'Signed Agreement')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
          <app-collapse-item
            title="Tax Deduction Remittances"
            class="card-item px-2"
          >
            <validation-observer ref="taxDeductionRemittancesRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Evidence of Tax (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Evidence of Tax"
                      rules="required"
                    >
                      <b-form-file
                        id="tax-deduction-remittances-file-name"
                        v-model="dueDiligence.supportingDocument.taxDeductionRemittances.taxRemittance"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_TAX)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Evidence of Pension (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Evidence of Pension"
                      rules="required"
                    >
                      <b-form-file
                        id="evidence-of-pension-file-name"
                        v-model="dueDiligence.supportingDocument.taxDeductionRemittances.pensionNHFRemittance"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_PENSION)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Evidence of Other Deduction Remittances (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)

                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Evidence of Other Deduction Remittances"
                      rules="required"
                    >
                      <b-form-file
                        id="other-deduction-remittances-file-name"
                        v-model="dueDiligence.supportingDocument.taxDeductionRemittances.otherDeductionRemittances"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.EVIDENCE_OF_OTHER_REMITTANCES)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.taxDeductionRemittances.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveTaxDeductionRemittances"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="taxDeductionRemittances.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="taxDeductionRemittances.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="taxDeductionRemittances.reviewedBy">
                    <strong>Reviewed By:</strong> {{ taxDeductionRemittances.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(taxDeductionRemittances.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('TaxAndDeductionRemittances', taxDeductionRemittances.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>
            <b-table
              ref="taxDeductionRef"
              :busy="isBusy"
              :items="taxDeductionList"
              responsive
              :fields="taxDeductionHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(evidenceOfTaxRemittances)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.evidenceOfTaxRemittances, 'Evidence Of Tax Remittances')"
                >
                  View Document
                </div>
              </template>
              <template #cell(evidenceOfPension)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.evidenceOfPension, 'Evidence Of Pension')"
                >
                  View Document
                </div>
              </template>
              <template #cell(evidenceOfOtherDeductions)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.evidenceOfOtherDeductions, 'Evidence Of OtherDeductions')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <app-collapse-item
            title="Environment and Social Risk"
            class="card-item px-2"
          >
            <validation-observer ref="environmentAndSocialRiskRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Environmental And Social RiskManagement Policies (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Environmental And Social RiskManagement Policies"
                      rules="required"
                    >
                      <b-form-file
                        id="environmental-and-social-risk-management-policies-file-name"
                        v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.environmentalAndSocialRiskManagementPolicies"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.ENVIRONMENTAL_AND_SOCIAL_RISK)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Relevant Policies Framework (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Relevant Policies Framework"
                      rules="required"
                    >

                      <b-form-file
                        id="relevant-policies-framework-file-name"
                        v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.relevantPoliciesFramework"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RELEVANT_POLICIES_FRAMEWORK)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Implementation Evidence (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Implementation Evidence"
                      rules="required"
                    >
                      <b-form-file
                        id="implementation-evidence-file-name"
                        v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.implementationEvidence"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.IMPLEMENTATION_EVIDENCE)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.environmentAndSocialRisk.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveEnvironmentAndSocialRisk"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="environmentAndSocialRisk.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="environmentAndSocialRisk.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="environmentAndSocialRisk.reviewedBy">
                    <strong>Reviewed By:</strong> {{ environmentAndSocialRisk.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(environmentAndSocialRisk.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('EnvironmentalAndSocialRisk', environmentAndSocialRisk.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>
            <b-table
              ref="environmentAndSocialRiskRef"
              :busy="isBusy"
              :items="environmentAndSocialRiskList"
              responsive
              :fields="environmentAndSocialRiskHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(environmentalAndSocialRiskManagementPolicies)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.environmentalAndSocialRiskManagementPolicies, 'Environmental And Social Risk Management Policies')"
                >
                  View Document
                </div>
              </template>
              <template #cell(relevantPoliciesFramework)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.relevantPoliciesFramework, 'Relevant Policies Framework')"
                >
                  View Document
                </div>
              </template>
              <template #cell(implementationEvidence)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.implementationEvidence, 'Implementation Evidence')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <app-collapse-item
            title="Advanced Risk Analysis"
            class="card-item px-2"
          >

            <validation-observer ref="advancedRiskAnalysisRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Stress Test Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Stress Test Reports"
                      rules="required"
                    >
                      <b-form-file
                        id="stress-test-reports-file-name"
                        v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.stressTestReports"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.STRESS_TEST_REPORTS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Market Sensitivity Analysis Reports (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Market Sensitivity Analysis Reports"
                      rules="required"
                    >
                      <b-form-file
                        id="market-sensitivity-analysis-reports-file-name"
                        v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.marketSensitivityAnalysisReports"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.MARKET_SENSITIVITY_ANALYSIS_REPORTS)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Liquidity Contingency Funding Plan (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Liquidity Contingency Funding Plan"
                      rules="required"
                    >
                      <b-form-file
                        id="liquidity-contingency-funding-plan-file-name"
                        v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.liquidityContingencyFundingPlan"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.LIQUIDITY_CONTINGENCY_FUNDING_PLAN)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Business Continuity And Disaster Recovery Plan (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Business Continuity And Disaster Recovery Plan"
                      rules="required"
                    >
                      <b-form-file
                        id="business-continuity-and-disaster-recovery-plan-file-name"
                        v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.BusinessContinuityAndDisasterRecoveryPlan"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.BUSINESS_CONTINUITY_AND_DISASTER_RECOVERY_PLAN)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>

                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="12">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.advancedRiskAnalysis.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveAdvancedRiskAnalysis"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="advancedRiskAnalysis.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="advancedRiskAnalysis.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="advancedRiskAnalysis.reviewedBy">
                    <strong>Reviewed By:</strong> {{ advancedRiskAnalysis.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(advancedRiskAnalysis.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('AdvancedRiskAnalysis', advancedRiskAnalysis.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="advancedRiskAnalysisRef"
              :busy="isBusy"
              :items="advancedRiskAnalysisList"
              responsive
              :fields="advancedRiskAnalysisHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >

              <template #cell(stressTestReports)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.stressTestReports, 'Environmental And Social Risk Management Policies')"
                >
                  View Document
                </div>
              </template>
              <template #cell(marketSensitivityAnalysisReports)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.marketSensitivityAnalysisReports, 'Relevant Policies Framework')"
                >
                  View Document
                </div>
              </template>
              <template #cell(liquidityContingencyFundingPlan)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.liquidityContingencyFundingPlan, 'Implementation Evidence')"
                >
                  View Document
                </div>
              </template>

              <template #cell(businessContinuityAndDisasterRecoveryPlan)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.businessContinuityAndDisasterRecoveryPlan, 'Implementation Evidence')"
                >
                  View Document
                </div>
              </template>

            </b-table>
          </app-collapse-item>

          <app-collapse-item
            title="Rating Agency Reports"
            class="card-item px-2"
          >

            <validation-observer ref="ratingAgencyReportsRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Reports of Rating Agencies (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Reports of Rating Agencies"
                      rules="required"
                    >
                      <b-form-file
                        id="reports-of-rating-agencies-file-name"
                        v-model="dueDiligence.supportingDocument.ratingAgencyReports.ratingAgencyReport"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.RATING_AGENCY_REPORT)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.ratingAgencyReports.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveRatingAgencyReports"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="ratingAgencyReports.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="ratingAgencyReports.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="ratingAgencyReports.reviewedBy">
                    <strong>Reviewed By:</strong> {{ ratingAgencyReports.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(ratingAgencyReports.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('RatingAgencyReport', ratingAgencyReports.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>

            <b-table
              ref="ratingAgencyReportsRef"
              :busy="isBusy"
              :items="ratingAgencyReportsList"
              responsive
              :fields="ratingAgencyReportsHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(ratingAgencyReport)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.ratingAgencyReport, 'Rating Agency Report')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>

          <app-collapse-item
            title="Internal Risk Monitoring"
            class="card-item px-2"
          >
            <validation-observer ref="internalRiskMonitoringRules">
              <b-form>
                <b-row class="match-height">
                  <b-col
                    md="6"
                    class="mb-2"
                  >
                    <p class="upload-label">
                      Risk Registers and ERM Risk Dashboard (Format accepted: jpg, png, jpeg, docx, pdf, xlsx, csv)
                    </p>
                    <ValidationProvider
                      v-slot="{ errors, valid }"
                      name="Risk Registers and ERM Risk Dashboard"
                      rules="required"
                    >

                      <b-form-file
                        id="risk-registers-file-name"
                        v-model="dueDiligence.supportingDocument.internalRiskMonitoring.riskRegisters"
                        label="Select File (.jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv format only)"
                        :state="errors.length > 0 ? false : (valid ? true : null)"
                        placeholder="Choose a file or drop it here..."
                        drop-placeholder="Drop file here..."
                        accept=".jpg, .png, .jpeg, .docx, .pdf, .xlsx, .csv"
                        :disabled="isReadonly"
                        multiple
                        @change="onSelectFile($event, DUE_DILIGENCE_UPLOAD_TYPES.INTERNAL_RISK_MONITORING)"
                      />
                      <b-form-invalid-feedback>
                        {{ errors[0] }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </b-col>

                </b-row>
                <b-row class="match-height">
                  <b-col md="6">
                    <TextAreaField
                      id="comment"
                      v-model="dueDiligence.supportingDocument.internalRiskMonitoring.comment"
                      rows="2"
                      label="Comment (Optional)"
                      placeholder="Enter Comment"
                      type="text"
                      :readonly="isReadonly"
                    />
                  </b-col>
                </b-row>

                <div class="w-100 d-flex justify-content-end">
                  <Button
                    label="Save"
                    size="md"
                    :loading="isLoading"
                    :disabled="isDisabled"
                    @click="saveInternalRiskMonitoring"
                  />
                </div>
                <b-row
                  v-if="lmsRole === 'Admin'"
                  class="match-height"
                >
                  <b-col md="12">
                    <RichTextAreaField
                      id="reviewer-comment"
                      v-model="internalRiskMonitoring.reviewerComment"
                      rows="2"
                      label="Reviewer Comment"
                      placeholder="Enter reviewer comment"
                      type="text"
                    />
                  </b-col>
                </b-row>
                <div
                  v-if="lmsRole === 'Admin'"
                  class="w-100 d-flex"
                  :class="internalRiskMonitoring.reviewedBy ? 'justify-content-between' : 'justify-content-end'"
                >
                  <span v-if="internalRiskMonitoring.reviewedBy">
                    <strong>Reviewed By:</strong> {{ internalRiskMonitoring.reviewedBy }} | <strong>Reviewed Date:</strong> {{ toDateWithTime(internalRiskMonitoring.dateReviewed) }}
                  </span>
                  <Button
                    label="Submit Comment"
                    size="md"
                    :loading="isReviewerLoading"
                    @click="submitReviewComment('InternalRiskMonitoring', internalRiskMonitoring.reviewerComment)"
                  />
                </div>
              </b-form>
            </validation-observer>
            <b-table
              ref="internalRiskMonitoringRef"
              :busy="isBusy"
              :items="internalRiskMonitoringList"
              responsive
              :fields="internalRiskMonitoringHeaders"
              primary-key="id"
              show-empty
              empty-text="No records available"
              class="position-relative mt-4"
            >
              <template #cell(internalRiskMonitoring)="data">
                <div
                  class="text-decoration-underline cursor-pointer text-primary"
                  @click="onViewDocumentFile(data.item.internalRiskMonitoring, 'Internal Risk Monitoring')"
                >
                  View Document
                </div>
              </template>
            </b-table>
          </app-collapse-item>
        </app-collapse>
      </b-tab>
    </b-tabs>
    <FullScreenSpinner :show="showSpinnerDoc" />

    <div
      v-if="lmsRole === 'Admin' && sourceChannel === 'pendingApprovals'"
      class="w-100 d-flex justify-content-end mt-4"
    >
      <Button
        v-if="getUserPermission('SETUP_CREDIT_LIMIT')"
        class="mr-2"
        label="Add Credit Limit"
        size="lg"
        :loading="isLoading"
        @click="showCreditLimit"
      />
      <!-- <Button
        label="Submit"
        size="lg"
        :loading="isLoading"
        :disabled="isDisabled"
        @click="handleDueDiligenceSubmit"
      /> -->
    </div>

    <b-modal
      id="view-document-modal"
      ref="avatarModal"
      no-close-on-backdrop
      :ok-title="$t('Download File')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <DocumentViewer
        :document-url="viewDocumentData"
        :loading="showSpinnerDoc"
        @loaded="onDocumentLoaded"
        @error="onDocumentError"
      />

    </b-modal>
    <!-- Credit Limit -->
    <b-modal
      id="credit-limit-modal"
      ref="creditLimitComponent"
      :title="modalCreateLimitTitle"
      ok-title="Cancel"
      ok-only
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      @hidden="resetCreditLimitFilter"
    >
      <CreditLimit
        :credit-limit-data="creditLimitFormData"
        :is-readonly="isReadonly"
        :button-label="creditLimitButton"
        :credit-limit-items="creditLimitList"
        :is-table-busy="isCreditLimitBusy"
        :per-page="pageSize"
        :current-page-prop="currentPage"
        @credit-limit-action="handleCreditLimit"
      />
    </b-modal>
    <!-- Credit Limit -->
    <!-- Display Validation Errors -->
    <b-modal
      id="all-validated-modal"
      ref="allValidatedModal"
      ok-title="Cancel"
      ok-only
      no-close-on-backdrop
      ok-variant="outline-secondary"
      centered
      size="md"
      title="Kindly attend to the field(s) below"
      @hidden="resetValidationModal"
    >
      <div
        v-for="(errorItem, index) in allValidationErrors"
        :key="index"
      >
        <p>{{ `${index + 1}.` }} <span style="color: red;"> {{ errorItem }}</span></p>
      </div>
    </b-modal>
    <!-- Display Validation Errors -->
  </div>
</template>

<script>
import {
  BRow, BCol, BTable, BForm, BTabs, BTab, BFormFile, VBTooltip, BFormInvalidFeedback,
} from 'bootstrap-vue'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import { v4 as uuidv4 } from 'uuid'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { toastAlert } from '@core/mixins/toast'
import { permissionMixin } from '@core/mixins/permission'
import Ripple from 'vue-ripple-directive'
import { modalMixins } from '@core/mixins/modal'
import { heightFade } from '@core/directives/animations'
import { tableMixins } from '@core/mixins/table'
import {
  DUE_DILIGENCE_ENUMS,
  DUE_DILIGENCE_UPLOAD_TYPES, processingConfig, saveConfig, documentPathMap,
} from '../../../helpers/constants'
import CreditLimit from './CreditLimit.vue'
import { lmsPermissions } from '../../../helpers/permissions'
import InputField from '../InputField.vue'
import InputNumberField from '../InputNumberField.vue'
import SelectField from '../SelectField.vue'
import TextAreaField from '../TextAreaField.vue'
import RichTextAreaField from '../RichTextAreaField.vue'
import { toDateWithYear, calculateDurationString, toDateWithTime } from '../../../helpers/formatDate'
import Button from '../../components/button/Button.vue'
import { toBalance, toKobo } from '../../../helpers/formatAmount'
import DatePickerField from '../DatePickerField.vue'
import { checkDueDiligenceCompleteness, addSpacesToLabel } from '@/helpers/misc'
import DocumentViewer from '@/views/components/DocumentViewer.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'

const currentYear = new Date().getFullYear()
const lastThreeYears = currentYear - 3
const lastTwoYears = currentYear - 2
const lastYear = currentYear - 1

export default {
  name: 'PFIApplication',
  components: {
    BRow,
    BCol,
    BTabs,
    BTab,
    BTable,
    BForm,
    ValidationObserver,
    ValidationProvider,
    BFormFile,
    InputField,
    InputNumberField,
    SelectField,
    TextAreaField,
    RichTextAreaField,
    Button,
    DatePickerField,
    AppCollapse,
    AppCollapseItem,
    CreditLimit,
    DocumentViewer,
    FullScreenSpinner,
    BFormInvalidFeedback,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
    'b-tooltip': VBTooltip,
  },
  mixins: [modalMixins, sweetAlert, permissionMixin, tableMixins, toastAlert],
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
    userType: {
      type: String,
      default: '',
    },
    channel: {
      type: String,
      default: '',
    },
    initialFormData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      toBalance,
      toDateWithTime,
      lastYear,
      lastTwoYears,
      lastThreeYears,
      toDateWithYear,
      sortBy: 'name',
      isBusy: false,
      showSpinner: false,
      showSpinnerDoc: false,
      isReadonly: false,
      required,
      isLoading: false,
      isReviewerLoading: false,
      isDisabled: false,
      sortDesc: false,
      DUE_DILIGENCE_UPLOAD_TYPES,
      pfiOnboardingList: [],
      finalPayload: [],
      fileExtension: '',
      viewDocumentModalTitle: '',
      uploadSupportingDocListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'description', sortable: true, label: 'Document Description' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'comment', label: 'Comment' },
        { key: 'dateLastModified', label: 'Upload Date' },
        { key: 'uploadedBy', label: 'Uploaded By' },
        { key: 'approvalStatus', label: 'Approval Status' },
        { key: 'actions', label: 'Actions' },
      ],
      uploadSupportingDocList: [],
      supportingDocumentType: '',
      uploadDocumentsData: {},
      documentIdentifier: '',
      datePickerConfig: {
        dateFormat: 'd-m-Y',
        maxDate: 'today',
        ariaDateFormat: 'Y-m-d',
      },
      qualificationsOptions: [
        { value: '1', label: 'BSc' },
        { value: '2', label: 'MSc' },
        { value: '3', label: 'PhD' },
        { value: '4', label: 'MBA' },
        { value: '5', label: 'CFA' },
        { value: '6', label: 'ACCA' },
        { value: '7', label: 'CPA' },
        { value: '8', label: 'CISA' },
        { value: '9', label: 'CISSP' },
        { value: '10', label: 'Other' },
      ],
      riskManagementStaffInformationList: [],
      riskManagementStaffInformationHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'designation', sortable: true, label: 'Designation' },
        { key: 'qualifications', label: 'Qualifications' },
        { key: 'relevantWorkingExperience', label: 'Relevant Working Experience' },
        { key: 'documentUrl', label: 'Document' },
      ],
      internalAuditFunctionStaffInformationList: [],
      internalAuditFunctionStaffInformationHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'designation', sortable: true, label: 'Designation' },
        { key: 'qualifications', label: 'Qualifications' },
        { key: 'relevantWorkingExperience', label: 'Relevant Working Experience' },
        { key: 'documentUrl', label: 'Document' },
      ],
      viewDocumentData: '',
      dueDiligence: {
        compulsoryDocument: {
          legalRegDocuments: {
            cacRegistration: [],
            articleOfAssociation: [],
            operatingLicense: [],
            comment: '',
          },
          boardManagement: {
            designation: '',
            duration: '',
            lastName: '',
            firstName: '',
            middleName: '',
            cvUpload: [],
            yearAppointed: '',
            cbnApprovalEvidence: [],
            comment: '',
          },
          leadershipGovernance: {
            bankOrganisationStructure: [],
            bankOrganisationChart: [],
            comment: '',
          },
          financialStatements: {
            auditedFinancialStatement1: [],
            auditedFinancialStatement2: [],
            auditedFinancialStatement3: [],
            comment: '',
          },
          recentFinancialPerformance: {
            latestFinancialPerformance: [],
            capitalAdequacyRatio: '',
            nonPerformingLoanRatio: '',
            liquidityRatio: '',
            loanToDepositRatio: '',
            comment: '',
          },
          creditRiskManagement: {
            creditRiskStrategyAndAnnualPortfolioPlan: [],
            comment: '',
          },
          riskManagementFramework: {
            riskManagementFrameworkUrl: [],
            policiesUrl: [],
            limitsUrl: [],
            manualUrl: [],
            comment: '',
          },
          loanPortfolioDetails: {
            creditPortfolioReport: [],
            comment: '',
          },
          aMLCFTCompliance: {
            antiMoneyLaundryUrl: [],
            counterTerroristFinancingPoliciesUrl: [],
            comment: '',
          },
          regulatoryCompliance: {
            regulatoryReturnsRenditionUrl: [],
            comment: '',
          },
          regulatoryExaminationReports: {
            cBNExaminationReportsUrl: [],
            nDICExaminationReportUrl: [],
            managementResponseToExaminationReportUrl: [],
            managementResponseToNDICExaminationReportsUrl: [],
            comment: '',
          },
          riskAndAuditStructure: {
            organogramUrl: [],
            riskManagementUnitStaffDetails: {
              staffDetails: [{
                fullName: '',
                designation: '',
                qualifications: [],
                relevantWorkingExperience: '',
              }],
              documentUrl: [],
            },
            internalAuditFunctionStaffDetails: {
              staffDetails: [{
                fullName: '',
                designation: '',
                qualifications: [],
                relevantWorkingExperience: '',
              }],
              documentUrl: [],

            },
            comment: '',
          },
          ongoingRiskMonitoring: {
            riskManagementReportsToBoardUrl: [],
            comment: '',
          },
          internalAuditFindings: {
            internalAuditsReportUrlLastYear: [],
            internalAuditsReportUrlTwoYearsAgo: [],
            internalAuditsReportUrlThreeYearsAgo: [],
            comment: '',
          },
          corporateGovernance: {
            corporateGovernanceUrl: [],
            comment: '',
          },
          collateralManagement: {
            collateralValuationUrl: [],
            nationalCollateralRegistryUrl: [],
            comment: '',
          },
          creditBureauAgreement: {
            evidenceOfAgreement: [],
            comment: '',
          },
        },
        // optionalDocuments: {
        //   creditBureauAgreement: {
        //     evidenceOfAgreement: [],
        //     comment: '',
        //   },
        //   customerComplaintsHandling: {
        //     customerComplaintsManagement: [],
        //     comment: '',
        //   },
        // },
        supportingDocument: {
          customerComplaintsHandling: {
            customerComplaintsManagement: [],
            comment: '',
          },
          consumerCreditProgram: {
            copiesConsumerCredit: [],
            comment: '',
          },
          consumerLoanPricing: {
            loanPricingStrategy: [],
            comment: '',
          },
          externalBoardAssessment: {
            recentExternalBoard: [],
            comment: '',
          },
          itSystemAgreements: {
            signedAgreement: [],
            comment: '',
          },
          taxDeductionRemittances: {
            taxRemittance: [],
            pensionNHFRemittance: [],
            otherDeductionRemittances: [],
            comment: '',
          },
          ratingAgencyReports: {
            ratingAgencyReport: [],
            comment: '',
          },
          internalRiskMonitoring: {
            ratingAgencyReport: [],
            riskRegisters: '',
          },
          environmentAndSocialRisk: {
            environmentalAndSocialRiskManagementPolicies: [],
            relevantPoliciesFramework: [],
            implementationEvidence: [],
            comment: '',
          },
          advancedRiskAnalysis: {
            stressTestReports: [],
            marketSensitivityAnalysisReports: [],
            liquidityContingencyFundingPlan: [],
            businessContinuityAndDisasterRecoveryPlan: [],
            comment: '',
          },
        },
      },
      legalDocumentList: [],
      legalDocumentHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'evidenceOfCACRegistration', sortable: true },
        { key: 'memorandum', sortable: true, label: 'Memorandum & Article of Association' },
        { key: 'operatingLicense', sortable: true, label: 'Operating License of CBN ' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      boardManagementList: [],
      boardManagementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'lastName', sortable: true },
        { key: 'firstName', sortable: true },
        { key: 'middleName', sortable: true },
        { key: 'designation', sortable: true },
        { key: 'cvUpload', sortable: true, label: 'Profile/CV Upload' },
        { key: 'cbnApprovalEvidence', sortable: true, label: 'CBN Approval Evidence' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'dateAppointed', sortable: true, label: 'Year Appointed' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      leadershipAndGovernanceList: [],
      leadershipAndGovernanceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'bankOrganistionStructure', sortable: true },
        { key: 'banksOrganisationalChart', sortable: true, label: "Bank's Organisational Chart" },
        { key: 'comment', sortable: true },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      financialStatementList: [],
      financialStatementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'auditedFinancialStatement1', sortable: true },
        { key: 'auditedFinancialStatement2', sortable: true },
        { key: 'auditedFinancialStatement3', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      recentFinancialPerformanceList: [],
      recentFinancialPerformanceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'latestFinancialPerformance', sortable: true, label: 'Latest Quarterly Management Accounts' },
        { key: 'capitalAdequacyRatio', sortable: true },
        { key: 'nonPerformingLoanRatio', sortable: true },
        { key: 'liquidityRatio', sortable: true },
        { key: 'loanToDepositRatio', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      riskManagementFrameworkList: [],
      riskManagementFrameworkHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'riskManagementFrameworkUrl', sortable: true, label: 'Risk Management Framework' },
        { key: 'policiesUrl', sortable: true, label: 'Policies' },
        { key: 'limitsUrl', sortable: true, label: 'Limits' },
        { key: 'manualUrl', sortable: true, label: 'Manuals' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      loanPortfolioDetailsList: [],
      loanPortfolioDetailsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'creditPortfolioReport', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      aMLCFTComplianceList: [],
      aMLCFTComplianceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'antiMoneyLaundryUrl', sortable: true, label: 'Anti-Money Laundering' },
        { key: 'counterTerroristFinancingPoliciesUrl', sortable: true, label: 'Counter Terrorist Financing Policies' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      creditRiskManagementList: [],
      creditRiskManagementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'creditRiskStrategyAndAnnualPortfolioPlan', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      regulatoryComplianceList: [],
      regulatoryComplianceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'regulatoryReturnsRenditionUrl', sortable: true, label: 'Regulatory Returns Rendition' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      regulatoryExaminationReportsList: [],
      regulatoryExaminationReportsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'cBNExaminationReportsUrl', sortable: true, label: 'CBN Examination Reports' },
        { key: 'nDICExaminationReportUrl', sortable: true, label: 'NDIC Examination Reports' },
        { key: 'managementResponseToExaminationReportUrl', sortable: true, label: 'Management Response To Examination Reports' },
        { key: 'managementResponseToNDICExaminationReportsUrl', sortable: true, label: 'Management Response To NDIC Examination Reports' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      riskAndAuditStructureList: [],
      riskAndAuditStructureHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'organogramUrl', sortable: true, label: 'Organogram' },
        { key: 'riskManagementUnitStaffDetails', sortable: true, label: 'Risk Management Unit Staff Details' },
        { key: 'internalAuditFunctionStaffDetails', sortable: true, label: 'Internal Audit Function Staff Details' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      ongoingRiskMonitoringList: [],
      ongoingRiskMonitoringHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'riskManagementReportsToBoardUrl', sortable: true, label: 'Risk Management Reports To Board' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      internalAuditFindingsList: [],
      internalAuditFindingsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'internalAuditsReportUrlLastYear', sortable: true, label: `Internal Audits Report ${lastYear}` },
        { key: 'internalAuditsReportUrlTwoYearsAgo', sortable: true, label: `Internal Audits Report ${lastTwoYears}` },
        { key: 'internalAuditsReportUrlThreeYearsAgo', sortable: true, label: `Internal Audits Report ${lastThreeYears}` },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      corporateGovernanceList: [],
      corporateGovernanceHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'corporateGovernanceUrl', sortable: true, label: 'Corporate Governance Policies & Code of Conduct' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      collateralManagementList: [],
      collateralManagementHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'collateralValuationUrl', sortable: true, label: 'Collateral Valuation' },
        { key: 'nationalCollateralRegistryUrl', sortable: true, label: 'National Collateral Registry' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      creditBureauList: [],
      creditBureauHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'evidenceOfAgreement', sortable: true, label: 'Evidence of Agreement Credit Bureaus' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      customerComplaintHandlingList: [],
      customerComplaintHandlingHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'customerComplaintManagement', sortable: true, label: 'Customer Complaint Management System/Framework Bureaus' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      consumerCreditList: [],
      consumerCreditHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'copiesConsumerCredit', sortable: true, label: 'Copies of Consumer Credit Product Programs' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      consumerLoanPricingList: [],
      consumerLoanPricingHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'loanPricingStrategy', sortable: true, label: 'Loan Pricing Strategy for Consumer Credit Customers' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      externalBoardAssessmentList: [],
      externalBoardAssessmentHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'mostRecentExternalBoardEvaluation', sortable: true, label: 'Most recent External Board Evaluation/Assessment Report' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      itSystemAgreementsList: [],
      itSystemAgreementsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'signedAgreement', sortable: true, label: 'Signed Agreement with Banking Application and Other IT Vendor' },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      taxDeductionList: [],
      taxDeductionHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'evidenceOfTaxRemittances', sortable: true },
        { key: 'evidenceOfPension', sortable: true },
        { key: 'evidenceOfOtherDeductions', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      ratingAgencyReportsList: [],
      ratingAgencyReportsHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'ratingAgencyReport', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      internalRiskMonitoringList: [],
      internalRiskMonitoringHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'internalRiskMonitoring', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      environmentAndSocialRiskList: [],
      environmentAndSocialRiskHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'environmentalAndSocialRiskManagementPolicies', sortable: true },
        { key: 'relevantPoliciesFramework', sortable: true },
        { key: 'implementationEvidence', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      advancedRiskAnalysisList: [],
      advancedRiskAnalysisHeaders: [
        { key: 'partnerCode', sortable: true },
        { key: 'stressTestReports', sortable: true },
        { key: 'marketSensitivityAnalysisReports', sortable: true },
        { key: 'liquidityContingencyFundingPlan', sortable: true },
        { key: 'businessContinuityAndDisasterRecoveryPlan', sortable: true },
        { key: 'comment', sortable: true, label: 'Comment' },
        { key: 'email', sortable: true },
        { key: 'dateModified', sortable: true },
      ],
      partnerCode: '',
      isCreditLimitBusy: false,
      creditLimitList: [],
      creditLimitHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'partnerCode', sortable: true, label: 'Partner Code' },
        { key: 'partnerName', sortable: true, label: 'Partner Name' },
        { key: 'approvedLimit', sortable: true, label: 'Approved Limit' },
        { key: 'status', sortable: true, label: 'Status' },
      ],
      creditLimitFormData: {
        creditLimit: null,
      },
      currentPartnerCode: '',
      creditLimitButton: 'Save',
      modalCreateLimitTitle: 'Set Credit Limit',

      legalRegDocuments: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      boardManagement: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      leadershipGovernance: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      financialStatements: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      recentFinancialPerformance: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      creditRiskManagement: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      riskManagementFramework: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      loanPortfolioDetails: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      aMLCFTCompliance: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      regulatoryCompliance: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      regulatoryExaminationReports: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      riskAndAuditStructure: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      ongoingRiskMonitoring: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      internalAuditFindings: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      corporateGovernance: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      collateralManagement: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      creditBureauAgreement: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      customerComplaintsHandling: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      consumerCreditProgram: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      consumerLoanPricing: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      externalBoardAssessment: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      itSystemAgreements: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      taxDeductionRemittances: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      environmentAndSocialRisk: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      advancedRiskAnalysis: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      ratingAgencyReports: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      internalRiskMonitoring: {
        reviewerComment: '',
        reviewedBy: '',
        dateReviewed: '',
      },
      allValidationErrors: [],
      dueDiligenceDatePickerConfig: {
        dateFormat: 'd-m-Y',
        maxDate: 'today',
        ariaDateFormat: 'd-m-Y',
      },
      lmsRole: '',
      sourceChannel: '',
    }
  },
  computed: {
    payloadString() {
    // Convert array to string for change detection
      return JSON.stringify(this.finalPayload)
    },
  },
  watch: {
    isEdit: {
      immediate: true,
      handler(val) {
        this.isReadonly = val
        this.isDisabled = val
      },
    },
    userType: {
      immediate: true,
      handler(val) {
        this.lmsRole = val
      },
    },
    channel: {
      immediate: true,
      handler(val) {
        this.sourceChannel = val
      },
    },
    initialFormData: {
      immediate: true,
      deep: true,
      handler(newValue) {
        try {
          this.partnerCode = newValue.partnerCode
          if (Object.keys(newValue).length) {
            const { partnerCode, dueDiligenceItems } = newValue

            const getCommonFields = sourceItem => ({
              partnerCode,
              comment: sourceItem?.value?.comment,
              email: sourceItem?.value?.uploadedBy,
              dateModified: sourceItem?.dateLastModified ? toDateWithYear(sourceItem?.dateLastModified) : null,
            })

            // Loop through the configuration to process each list using Object.keys().forEach()
            Object.keys(processingConfig).forEach(listName => {
              const configItem = processingConfig[listName]
              const sourceItem = dueDiligenceItems.find(val => val.label === configItem.sourceLabel)

              if (sourceItem) { // Only process if the source item is found
                const commonFields = getCommonFields(sourceItem)

                if (configItem.collectionPath) { // Handle collections (like board)
                  const sourceCollection = sourceItem.value?.[configItem.collectionPath]
                  if (Array.isArray(sourceCollection)) {
                  // Map items within the collection
                    this[listName] = sourceCollection.map(collectionItem => {
                      const mappedItem = {}
                      // Iterate over collectionFieldMap using Object.keys().forEach()
                      Object.keys(configItem.collectionFieldMap).forEach(targetKey => {
                        const sourceKey = configItem.collectionFieldMap[targetKey]
                        mappedItem[targetKey] = collectionItem?.[sourceKey]
                      })
                      // Combine mapped item properties with common fields
                      return { ...mappedItem, ...commonFields }
                    })
                  } else {
                    // If collection is missing or not an array, assign empty array
                    this[listName] = []
                  }
                } else if (configItem.fieldMap) { // Handle single items
                  const mappedObject = {}
                  // Map fields from the source item's value using Object.keys().forEach()
                  Object.keys(configItem.fieldMap).forEach(targetKey => {
                    const sourceKey = configItem.fieldMap[targetKey]
                    mappedObject[targetKey] = sourceItem.value?.[sourceKey]
                  })
                  // Combine mapped object properties with common fields and wrap in an array
                  this[listName] = [{ ...mappedObject, ...commonFields }]
                } else {
                // Should not happen with the current config, but good practice
                  this[listName] = []
                }
              } else {
                // If the source item for this label is not found, initialize the list as empty
                this[listName] = []
                console.warn(`Due diligence item with label ${configItem.sourceLabel} not found in response data.`)
              }
            })

            this.finalPayload = dueDiligenceItems?.map(val => ({
              label: val.label,
              value: val.value,
            }))
            this.fetchReviewComments()
          }
        } catch (error) {
          //  console.log({ error })
        }
      },
    },
    payloadString: {
      handler() {
        this.emitFormUpdate()
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      uploadDocumentImages: 'account/uploadDocumentImagesAction',
      validateBVN: 'eChannels/validateBVNAction',
      saveOrUpdateDueDiligence: 'llms/saveOrUpdateDueDiligenceAction',
      getDueDiligenceList: 'llms/getDueDiligenceList',
      submitDueDiligence: 'llms/submitDueDiligence',
      downloadOfferLetter: 'reports/downloadLoanApplicationLetter',
      getCreditLimits: 'account/getCreditLimits',
      createCreditLimit: 'account/createCreditLimit',
      saveReviewerComment: 'account/saveReviewerComment',
      getReviewComments: 'account/getReviewComments',
    }),
    emitFormUpdate() {
      const formData = this.getFormData()
      this.$emit('update', {
        formData: { ...formData },
      })
    },
    resetValidationModal() {
      this.allValidationErrors = []
    },
    async fetchReviewComments(partnerCode) {
      try {
        const response = await this.getReviewComments({
          partnerCode: this.partnerCode || partnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.mapRecordsToStructure(data)
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    mapRecordsToStructure(records) {
      records.forEach(record => {
        const {
          label, reviewerComment, reviewedBy, dateReviewed,
        } = record

        switch (label) {
          case 'LegalAndRegistrationDocument':
            this.legalRegDocuments = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'BoardAndManagementInformation':
            this.boardManagement = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'LeadershipAndGovernanceStructure':
            this.leadershipGovernance = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'FinancialStatement':
            this.financialStatements = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'RecentFinancialPerformance':
            this.recentFinancialPerformance = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'CreditRiskManagement':
            this.creditRiskManagement = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'RiskManagementFramework':
            this.riskManagementFramework = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'LoanPortfolioDetails':
            this.loanPortfolioDetails = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'AMLCFTCompliance':
            this.aMLCFTCompliance = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'RegulatoryCompliance':
            this.regulatoryCompliance = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'RegulatoryExaminationReports':
            this.regulatoryExaminationReports = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'RiskAndAuditStructure':
            this.riskAndAuditStructure = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'OngoingRiskMonitoring':
            this.ongoingRiskMonitoring = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'InternalAuditFindings':
            this.internalAuditFindings = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'CorporateGovernance':
            this.corporateGovernance = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'CollateralManagement':
            this.collateralManagement = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'CreditBureauAgreement':
            this.creditBureauAgreement = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'CustomerComplaintsHandling':
            this.customerComplaintsHandling = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'CustomerCreditPrograms':
            this.consumerCreditProgram = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'ConsumerLoanPricing':
            this.consumerLoanPricing = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'ExternalBoardAssessment':
            this.externalBoardAssessment = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'ITSystemAgreements':
            this.itSystemAgreements = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'TaxAndDeductionRemittances':
            this.taxDeductionRemittances = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'EnvironmentalAndSocialRisk':
            this.environmentAndSocialRisk = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'AdvancedRiskAnalysis':
            this.advancedRiskAnalysis = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'RatingAgencyReport':
            this.ratingAgencyReports = { reviewerComment, reviewedBy, dateReviewed }
            break
          case 'InternalRiskMonitoring':
            this.internalRiskMonitoring = { reviewerComment, reviewedBy, dateReviewed }
            break
          default:
            break
        }
      })
    },
    async submitReviewComment(label, reviewerComment) {
      try {
        this.isReviewerLoading = true

        const formData = {
          label,
          partnerCode: this.partnerCode,
          reviewerComment,
        }

        const response = await this.saveReviewerComment({
          formData,
          permission: lmsPermissions.ADD_DUE_DILIGENCE_REVIEW,
        })
        const { status } = await response
        if (status === 200) {
          this.fetchReviewComments(this.partnerCode)
          this.alertSuccess({ text: this.$t('Your request to Save Reviewer Comment was successful') })
        }
      } catch (error) {
        this.isReviewerLoading = false
        this.handleError(error)
      } finally {
        this.isReviewerLoading = false
      }
    },
    addNewRiskManagementUnitStaffDetails() {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.staffDetails.push({
        fullName: '',
        designation: '',
        qualifications: [],
        relevantWorkingExperience: '',
      })
    },
    removeRiskManagementUnitStaffDetails(index) {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.riskManagementUnitStaffDetails.staffDetails.splice(index, 1)
    },
    addNewInternalAuditFunctionStaffDetails() {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.staffDetails.push({
        fullName: '',
        designation: '',
        qualifications: [],
        relevantWorkingExperience: '',
      })
    },
    removeInternalAuditFunctionStaffDetails(index) {
      this.dueDiligence.compulsoryDocument.riskAndAuditStructure.internalAuditFunctionStaffDetails.staffDetails.splice(index, 1)
    },
    onRiskManagementUnitStaffDetails(items) {
      this.riskManagementStaffInformationList = items.staffDetails.map(item => ({
        ...item,
        qualifications: this.qualificationsOptions
          .filter(opt => item.qualifications.includes(Number(opt.value)))
          .map(opt => opt.label).join(', '),
        documentUrl: items.documentUrl,
      }))
      this.$root.$emit('bv::show::modal', 'risk-management-unit-staff-details-modal')
    },
    onInternalAuditFunctionStaffDetails(items) {
      this.internalAuditFunctionStaffInformationList = items.staffDetails.map(item => ({
        ...item,
        qualifications: this.qualificationsOptions
          .filter(opt => item.qualifications.includes(Number(opt.value)))
          .map(opt => opt.label).join(', '),
        documentUrl: items.documentUrl,
      }))
      this.$root.$emit('bv::show::modal', 'internal-audit-unit-staff-details-modal')
    },
    extractFileFromEvent(event) {
      if (event.target && event.target.files && event.target.files.length > 0) {
        return event.target.files[0]
      } if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        return event.dataTransfer.files[0] // For drag and drop
      } if (event instanceof File) {
        return event // In case the file object is passed directly
      } if (event instanceof FileList && event.length > 0) {
        return event[0] // In case a FileList is passed
      } if (Array.isArray(event) && event.length > 0 && event[0] instanceof File) {
        return event[0] // In case an array of Files is passed
      }
      return null
    },

    async onSelectFile(event, type) {
      try {
        // 1. Extract the file using a helper method
        const file = this.extractFileFromEvent(event)

        if (!file) {
          console.error('No file selected')
          return
        }

        // Get the target path from the map
        const targetPath = documentPathMap[type]
        if (!targetPath) {
          this.alertError({ text: this.$t('An error occurred (unknown upload type).') })
          return
        }

        this.showSpinnerDoc = true
        // 2. Prepare and send the upload request
        const formData = new FormData()
        formData.append('attachedFile', file)
        this.documentIdentifier = uuidv4()
        formData.append('type', 'Others')
        formData.append('referenceNumber', this.documentIdentifier)

        const response = await this.uploadDocumentImages({
          formData,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })

        // 3. Handle the response
        const { status, data } = response
        if (status === 200 && data.url) {
          // Update the appropriate field using a mapping object instead of a switch
          this.setNestedProperty(this.dueDiligence, targetPath, data.url)
          this.toastAlert({
            title: this.$t('Document uploaded successfully'),
          })
          this.showSpinnerDoc = false
        } else {
          // Handle non-200 status or missing URL in response
          const errorMessage = data?.message || this.$t('Failed to upload document. Please try again.')
          this.alertError({ text: errorMessage })
          this.showSpinnerDoc = false
        }
      } catch (error) {
        this.handleUploadError(error)
        this.showSpinnerDoc = false
      }
    },

    handleUploadError(error) {
      if (error.response && error.response.data && error.response.data.errors) {
        const text = this.$t('Your request to upload image failed. Reason:')
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `${text} ${responseError.errorMessage}` })
        })
      } else {
        this.alertError({ text: this.$t('Failed to upload document. Please try again.') })
      }
    },
    async onViewDueDiligence(item) {
      try {
        this.showSpinner = true
        this.partnerCode = item?.partnerCode

        const response = await this.getDueDiligenceList({
          search: { partnerCode: item.partnerCode },
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })

        const { status, data } = response

        if (status === 200 && data) {
          this.finalPayload = data?.map(val => ({
            label: val.label,
            value: val.value,
          }))

          // Helper to extract common fields from a source item
          const getCommonFields = sourceItem => ({
            partnerCode: sourceItem?.partnerCode,
            comment: sourceItem?.value?.comment,
            email: sourceItem?.value?.uploadedBy,
            dateModified: sourceItem?.dateLastModified ? toDateWithYear(sourceItem?.dateLastModified) : null,
          })

          // Loop through the configuration to process each list using Object.keys().forEach()
          Object.keys(processingConfig).forEach(listName => {
            const configItem = processingConfig[listName]
            const sourceItem = data.find(val => val.label === configItem.sourceLabel)

            if (sourceItem) { // Only process if the source item is found
              const commonFields = getCommonFields(sourceItem)

              if (configItem.collectionPath) { // Handle collections (like board)
                const sourceCollection = sourceItem.value?.[configItem.collectionPath]
                if (Array.isArray(sourceCollection)) {
                  // Map items within the collection
                  this[listName] = sourceCollection.map(collectionItem => {
                    const mappedItem = {}
                    // Iterate over collectionFieldMap using Object.keys().forEach()
                    Object.keys(configItem.collectionFieldMap).forEach(targetKey => {
                      const sourceKey = configItem.collectionFieldMap[targetKey]
                      mappedItem[targetKey] = collectionItem?.[sourceKey]
                    })
                    // Combine mapped item properties with common fields
                    return { ...mappedItem, ...commonFields }
                  })
                } else {
                // If collection is missing or not an array, assign empty array
                  this[listName] = []
                }
              } else if (configItem.fieldMap) { // Handle single items
                const mappedObject = {}
                // Map fields from the source item's value using Object.keys().forEach()
                Object.keys(configItem.fieldMap).forEach(targetKey => {
                  const sourceKey = configItem.fieldMap[targetKey]
                  mappedObject[targetKey] = sourceItem.value?.[sourceKey]
                })
                // Combine mapped object properties with common fields and wrap in an array
                this[listName] = [{ ...mappedObject, ...commonFields }]
              } else {
                // Should not happen with the current config, but good practice
                this[listName] = []
              }
            } else {
            // If the source item for this label is not found, initialize the list as empty
              this[listName] = []
              console.warn(`Due diligence item with label ${configItem.sourceLabel} not found in response data.`)
            }
          }) // End Object.keys(processingConfig).forEach
        } else {
        // Handle non-200 status or missing data if necessary
          console.error('Failed to fetch due diligence data:', response)
        }
      } catch (error) {
        console.error('Error fetching or processing due diligence data:', error)
      } finally {
        this.showSpinner = false
        // Ensure the modal is shown even on error, or handle differently if needed
        this.$root.$emit('bv::show::modal', 'due-diligence-modal')
      }
    },

    async onSelectYearAppointed(dateStr) {
      const selectedDateStr = Array.isArray(dateStr) ? dateStr[0] : dateStr

      // Call the helper function to calculate the duration string
      const durationString = calculateDurationString(selectedDateStr)

      this.dueDiligence.compulsoryDocument.boardManagement.duration = durationString
    },
    // Helper to safely get nested property values
    getNestedProperty(obj, path) {
      if (!path) return obj
      const pathParts = path.split('.')
      return pathParts.reduce((current, part) => current?.[part], obj)
    },

    // Helper to safely set nested property values (includes no-plusplus fix)
    setNestedProperty(obj, path, value) {
      if (!path) return
      const pathParts = path.split('.')
      let current = obj
      for (let i = 0; i < pathParts.length - 1; i += 1) { // Corrected i++ to i += 1
        if (current === null || typeof current !== 'object') {
          console.error(`Cannot set property: Path "${path}" is invalid at "${pathParts.slice(0, i + 1).join('.')}"`)
          return
        }
        if (!current[pathParts[i]] && i < pathParts.length - 1) {
          console.error(`Cannot set property: Path "${path}" does not exist at "${pathParts.slice(0, i + 1).join('.')}"`)
          return
        }
        current = current[pathParts[i]]
      }
      if (current !== null && typeof current === 'object') {
        current[pathParts[pathParts.length - 1]] = value
      } else {
        console.error(`Cannot set property: Parent path "${pathParts.slice(0, -1).join('.')}" is not an object or is null.`)
      }
    },

    // Helper to extract common fields from the response data returned by saveOrUpdateDueDiligence
    getCommonFieldsFromResponseData(data) {
      return {
        partnerCode: data?.partnerCode,
        comment: data?.value?.comment,
        email: data?.value?.uploadedBy,
        dateModified: data?.dateLastModified ? toDateWithYear(data?.dateLastModified) : null,
      }
    },

    // --- Generic Save Method ---
    // This method contains the common logic for saving due diligence items.
    async saveDueDiligenceItem(configName) {
      const config = saveConfig[configName]

      if (!config) {
        console.error(`Save configuration not found for: ${configName}`)
        this.alertError({ text: this.$t('An error occurred while saving.') })
        return
      }

      const validator = this.$refs[config.validationRef]

      if (!validator) {
        console.error(`Validation ref "${config.validationRef}" not found.`)
        this.alertError({ text: this.$t('An internal error occurred.') })
        return
      }

      validator.validate().then(async success => {
        if (success) {
          this.confirm(async () => {
            try {
              this.showSpinner = true

              // Determine the base source object (supportingDocument or compulsoryDocument)
              const resetPathParts = config.resetPath.split('.')
              const sourceDocKey = resetPathParts[0]
              const sourceDataObject = this.getNestedProperty(this.dueDiligence, sourceDocKey)

              if (!sourceDataObject) {
                console.error(`Base source object "${sourceDocKey}" not found on dueDiligence.`)
                this.alertError({ text: this.$t('An internal error occurred (source data missing).') })
                this.showSpinner = false
                return
              }

              const payload = {
                label: config.label,
                value: config.payloadValue(sourceDataObject), // Pass the base source object
                partnerCode: this.partnerCode,
              }
              this.showSpinnerDoc = true
              const response = await this.saveOrUpdateDueDiligence(payload)
              const { status, data } = response

              if (status === 200 && data) {
                this.showSpinnerDoc = false
                const listConfig = processingConfig[config.processingConfigKey]

                const itemIndex = this.finalPayload.findIndex(item => item.label === config.label)

                if (itemIndex > -1) {
                  this.$set(this.finalPayload, itemIndex, {
                    label: data.label,
                    value: data.value,
                  })
                } else {
                  this.finalPayload.push({
                    label: data.label,
                    value: data.value,
                  })
                }

                if (!listConfig) {
                  console.error(`No list mapping configuration found in processingConfig for key "${config.processingConfigKey}". List not updated.`)
                  this.onViewDueDiligence({ partnerCode: this.partnerCode }) // Fallback
                } else if (config.updateType === 'collection' && listConfig.collectionPath) {
                  // --- Handle updating a collection list (like board) ---
                  // We expect the API response 'data' to contain the *entire* updated collection in data.value[listConfig.collectionPath]
                  const sourceCollection = data.value?.[listConfig.collectionPath]

                  if (Array.isArray(sourceCollection)) {
                    const commonFields = this.getCommonFieldsFromResponseData(data)
                    this[config.listProperty] = sourceCollection.map(collectionItem => {
                      const mappedItem = {}
                      Object.keys(listConfig.collectionFieldMap).forEach(targetKey => {
                        const sourceKey = listConfig.collectionFieldMap[targetKey]
                        mappedItem[targetKey] = collectionItem?.[sourceKey]
                      })

                      return { ...mappedItem, ...commonFields }
                    })
                  } else {
                    console.error(`Response data for "${config.label}" did not contain a valid collection array at "${listConfig.collectionPath}". List not updated.`)
                    this[config.listProperty] = []
                    this.onViewDueDiligence({ partnerCode: this.partnerCode }) // Fallback
                  }
                } else if (listConfig.fieldMap) {
                  // --- Handle updating a standard single item list ---
                  const updatedListItem = this.getCommonFieldsFromResponseData(data)
                  Object.keys(listConfig.fieldMap).forEach(targetKey => {
                    const sourceKey = listConfig.fieldMap[targetKey]
                    updatedListItem[targetKey] = data.value?.[sourceKey]
                  })
                  // Assign the new item (wrapped in an array) to the correct list property
                  // NOTE: Corrected original bug where some lists were assigned an object instead of an array
                  this[config.listProperty] = [updatedListItem]
                } else {
                  console.error(`Processing config for "${config.processingConfigKey}" has neither fieldMap nor collectionPath.`)
                  this.onViewDueDiligence({ partnerCode: this.partnerCode }) // Fallback
                }

                // Reset the source data in the form using the full resetPath
                this.setNestedProperty(this.dueDiligence, config.resetPath, config.resetValue)

                this.alertSuccess({ text: this.$t(config.successMessageKey) })
                this.emitFormUpdate()
              } else {
                const errorMessage = data?.message || this.$t('Failed to save due diligence item.')
                this.alertError({ text: errorMessage })
                console.error('Save failed:', response)
              }
            } catch (error) {
              console.error(`Error saving ${config.label}:`, error)
              this.alertError({ text: this.$t('An error occurred while saving.') })
            } finally {
              this.showSpinner = false
              this.showSpinnerDoc = false
            }
          }, { text: config.confirmMessage })
        } else {
          const errorMessages = []
          Object.keys(validator.errors).forEach(key => {
            const value = validator.errors[key]

            if (Array.isArray(value) && value.length > 0) {
              value.forEach(error => {
                errorMessages.push(`${error}`)
              })
            }
          })
          this.allValidationErrors = errorMessages
          this.$root.$emit('bv::show::modal', 'all-validated-modal')
        }
      })
    },
    async saveLegalRegDocuments() {
      await this.saveDueDiligenceItem('saveLegalRegDocuments')
    },

    async saveLegalGovernance() {
      await this.saveDueDiligenceItem('saveLegalGovernance')
    },

    async saveBoardManagementInfo() {
      await this.saveDueDiligenceItem('saveBoardManagementInfo')
    },

    async saveFinancialStatements() {
      await this.saveDueDiligenceItem('saveFinancialStatements')
    },

    async saveRecentFinancialPerformance() {
      await this.saveDueDiligenceItem('saveRecentFinancialPerformance')
    },

    async saveRiskManagementFramework() {
      await this.saveDueDiligenceItem('saveRiskManagementFramework')
    },
    async saveCreditRiskManagement() {
      await this.saveDueDiligenceItem('saveCreditRiskManagement')
    },
    async saveLoanPortfolioDetails() {
      await this.saveDueDiligenceItem('saveLoanPortfolioDetails')
    },

    async saveAMLCFTCompliance() {
      await this.saveDueDiligenceItem('saveAMLCFTCompliance')
    },

    async saveRiskAndAuditStructure() {
      await this.saveDueDiligenceItem('saveRiskAndAuditStructure')
    },

    async saveOngoingRiskMonitoring() {
      await this.saveDueDiligenceItem('saveOngoingRiskMonitoring')
    },

    async saveInternalAuditFindings() {
      await this.saveDueDiligenceItem('saveInternalAuditFindings')
    },

    async saveCorporateGovernance() {
      await this.saveDueDiligenceItem('saveCorporateGovernance')
    },

    async saveCollateralManagement() {
      await this.saveDueDiligenceItem('saveCollateralManagement')
    },

    async saveCreditBureau() {
      await this.saveDueDiligenceItem('saveCreditBureau')
    },

    async saveCustomerComplaintsHandling() {
      await this.saveDueDiligenceItem('saveCustomerComplaintsHandling')
    },

    async saveConsumerCreditProgram() {
      await this.saveDueDiligenceItem('saveConsumerCreditProgram')
    },

    async saveConsumerLoanPricing() {
      await this.saveDueDiligenceItem('saveConsumerLoanPricing')
    },

    async saveRatingAgencyReports() {
      await this.saveDueDiligenceItem('saveRatingAgencyReports')
    },

    async saveExternalBoardAssessment() {
      await this.saveDueDiligenceItem('saveExternalBoardAssessment')
    },

    async saveITSystemAgreement() {
      await this.saveDueDiligenceItem('saveITSystemAgreement')
    },

    async saveTaxDeductionRemittances() {
      await this.saveDueDiligenceItem('saveTaxDeductionRemittances')
    },

    async saveRegulatoryCompliance() {
      await this.saveDueDiligenceItem('saveRegulatoryCompliance')
    },

    async saveRegulatoryExaminationReports() {
      await this.saveDueDiligenceItem('saveRegulatoryExaminationReports')
    },

    async saveInternalRiskMonitoring() {
      await this.saveDueDiligenceItem('saveInternalRiskMonitoring')
    },
    async saveEnvironmentAndSocialRisk() {
      await this.saveDueDiligenceItem('saveEnvironmentAndSocialRisk')
    },
    async saveAdvancedRiskAnalysis() {
      await this.saveDueDiligenceItem('saveAdvancedRiskAnalysis')
    },

    getFormData() {
      const formData = {
        dueDiligenceItems: this.finalPayload,
        partnerCode: this.partnerCode,
      }
      return formData
    },

    async dueDiligenceSubmit() {
      const completenessCheck = checkDueDiligenceCompleteness(
        this.finalPayload,
        DUE_DILIGENCE_ENUMS,
      )

      if (!completenessCheck.isComplete) {
        const missingCount = completenessCheck.missingLabels.length
        const formattedMissingList = completenessCheck.missingLabels
          .map(label => addSpacesToLabel(label)).sort()
          .join('<br> ')

        const errorMessage = `Cannot submit Due Diligence. <br>${missingCount} required document(s) missing: <br> <br>${formattedMissingList}. <br> <br>Please ensure all compulsory and supporting documents sections are completed.`

        this.alertError({ html: this.$t(errorMessage) })

        return
      }

      const payload = this.getFormData()

      try {
        this.showSpinner = true

        const response = await this.submitDueDiligence(payload)
        const { status } = response

        if (status === 200) {
          this.hideModal('due-diligence-modal')
          this.alertSuccess({ text: this.$t('Your request to submit Due Diligence was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
      this.emitFormUpdate()
    },

    async showCreditLimit(partnerCode) {
      try {
        this.$root.$emit('bv::show::modal', 'credit-limit-modal')
        this.isCreditLimitBusy = true
        const response = await this.getCreditLimits({
          partnerCode: this.partnerCode || partnerCode,
          permission: lmsPermissions.VIEW_PFI_ONBOARDING,
        })
        const { status, data } = response
        if (status === 200 && data) {
          this.creditLimitFormData = {
            creditLimit: data.approvedLimit ? toBalance(data.approvedLimit) : '',
          }
          // Convert Objects to array of object
          const result = {
            data: [data],
          }
          this.creditLimitList = result.data[0].approvedLimit > 0 ? result.data : []
          if (this.creditLimitList.length > 0) {
            this.creditLimitButton = 'Update'
          }
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isCreditLimitBusy = false
      }
    },
    async handleCreditLimit(formData) {
      this.confirm(() => this.createLimitAction(formData), { text: `Are you sure you want to ${this.creditLimitButton} this Credit Limit?` })
    },
    async createLimitAction(val) {
      try {
        this.isCreditLimitBusy = true

        const formData = {
          creditLimit: val.creditLimit ? toKobo(val.creditLimit) : '',
          partnerCode: this.partnerCode,
        }
        const response = await this.createCreditLimit({ formData, permission: lmsPermissions.SETUP_CREDIT_LIMIT })
        const { status } = await response
        if (status === 200) {
          this.showCreditLimit(this.partnerCode)
          this.alertSuccess({ text: `Your request to ${this.creditLimitButton} Credit Limit was successful` })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to ${this.creditLimitButton} Credit Limit failed. Reason: ${responseError.errorMessage}` })
        })
      } finally {
        this.isCreditLimitBusy = false
      }
    },
    async handleDueDiligenceSubmit() {
      // bvModalEvt.preventDefault()
      this.confirm(this.dueDiligenceSubmit, { text: 'Are you sure you want to submit the due diligence form?' })
    },

    resetFilters() {
      this.filter.institutionName = ''
      this.filter.email = ''
      this.filter.partnerCode = ''
    },
    resetCreditLimitFilter() {
      this.creditLimitList = []
      this.creditLimitFormData.creditLimit = ''
      this.creditLimitButton = 'Save'
    },

    handleDownload(url) {
      try {
        const link = document.createElement('a')
        link.href = url

        // Extract filename from URL or use title
        const filename = new URL(url).pathname.split('/').pop()
      || this.viewDocumentModalTitle.replace(/\s+/g, '_')

        link.setAttribute('download', filename)
        link.style.display = 'none'
        document.body.appendChild(link)

        // Trigger download
        link.click()
        this.alertSuccess({ text: 'Your request to download file was successful' })

        // Clean up
        document.body.removeChild(link)
      } catch (error) {
        this.alertError({ text: 'Download may have been blocked. Please check your browser.' })
      }
    },
    onViewDocumentFile(url, title) {
      // Reset previous state
      this.showSpinnerDoc = true
      this.fileExtension = null
      this.viewDocumentData = null
      const params = new URL(url).searchParams
      const fileName = params.get('DocumentId') // get full name
      const ext = fileName.split('.').pop() // extract extension'

      this.fileExtension = ext
      // this.viewDocumentData = url
      this.viewDocumentModalTitle = title

      this.loadDocument(url)
      // this.$root.$emit('bv::show::modal', 'view-document-modal')
    },
    onDocumentLoaded() {
      this.showSpinnerDoc = false
    },
    onDocumentError() {
      this.showSpinnerDoc = false
      // this.alertError({ text: 'Failed to load document.' })
    },
    loadDocument(url) {
      // For images, preload to handle loading state
      if (['png', 'jpeg', 'jpg', 'bmp'].includes(this.fileExtension)) {
        const img = new Image()
        img.onload = () => {
          this.viewDocumentData = url
          this.showSpinnerDoc = false
          this.$root.$emit('bv::show::modal', 'view-document-modal')
        }
        img.onerror = () => {
          this.showSpinnerDoc = false
          this.alertError({ text: 'Failed to load image' })
        }
        img.src = url
      } else { // For PDFs and other files
        this.viewDocumentData = url
        this.showSpinnerDoc = false
        this.$root.$emit('bv::show::modal', 'view-document-modal')
      }
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
  },

}
</script>
